import cuid from 'cuid'
import Storage from 'disco-storage'
import merge from 'lodash/merge'
import omitBy from 'lodash/omitBy'
import pick from 'lodash/pick'
import mapSeries from 'promise-map-series'
import { isLoggingInFromAccountsSubdomain } from '@disco/api/utils/auth'
import type { Artist, ArtistId } from 'types/artist'
import type { BrowseResponse } from 'types/browse'
import type { BusinessData, BusinessDataPatchRequest } from 'types/business'
import type { AddToDiscoParams } from 'types/api'
import type { RecipientListId } from 'types/list'
import type {
  EmailCampaign,
  EmailCampaignId,
  EmailCampaignStats,
} from 'types/campaign'
import type {
  ClientLibrary,
  ClientLibraryArtist,
  ClientLibraryClient,
  ClientLibraryContentGroupInput,
  ClientLibraryContentItem,
  ClientLibraryDiscoveryPanelItem,
  ClientLibraryId,
  ClientLibraryInvite,
} from 'types/client-library'
import type { Group, GroupId, BusinessShare } from 'types/group'
import type {
  LicenseRequestSubmissionsPage,
  RequestFormVersion,
} from 'types/license-requests'
import type {
  Contact,
  ContactBulkChanges,
  ContactId,
  ContactNote,
  Recipient,
  RecipientList,
} from 'types/list'
import type { ItemWithUpdatedFields } from 'types/nesting'
import type { DismissibleNotification } from 'types/notification'
import type {
  Playlist,
  PlaylistId,
  PlaylistTag,
  PlaylistTheme,
  PlaylistVersionId,
  ZipUrls,
} from 'types/playlist'
import type { Shortlist, TempItem } from 'types/shortlist'
import type { TrackPlaylistsResponse, V2SearchQuery } from 'types/search-page'
import type {
  ChargebeeAddon,
  ChargebeeAddOnDetailed,
  ChargebeeAddonEstimateResponse,
  SubscriptionData,
} from 'types/subscription'
import type {
  NativeTag,
  NestingLabel,
  SelectableTrack,
  Track,
  TrackId,
} from 'types/track'
import type { EventSource, TrackEventOptions } from 'types/ui'
import type {
  BusinessUser,
  LoginDetails,
  ServerBusinessInvite,
  Settings,
} from 'types/user'
import type { ExternalIdentifier } from 'types/writer'
import type { GROUP_TYPE } from '@disco/constants/GroupTypes'
import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import type {
  USER,
  BUSINESS,
  RECIPIENT_GROUP,
  BRIEF,
} from '@disco/constants/RecipientTypes'
import {
  PLAYLIST_DATE_SORT,
  SORT_DIRECTION_ASC,
  TRACK_DATE_SORT,
} from '@disco/constants/SortTypes'
import type { ViewType } from '@disco/constants/ViewTypes'
import {
  ALBUM_VIEW,
  ARTIST_VIEW,
  BRIEF_ALL_VIEW,
  BRIEF_INBOX_VIEW,
  BRIEF_OPEN_VIEW,
  BRIEF_CLOSED_VIEW,
  BRIEF_MINE_VIEW,
  DEFAULT_VIEW,
  PLAYLIST_VIEW,
  TRACK_VIEW,
} from '@disco/constants/ViewTypes'

import { __REACT_NATIVE__, getEnvName } from '@disco/constants/environment'
import type { TempTrack } from 'types/shortlist'
import { getSortParam } from './helpers'
import type { TrackFormatSelection, LyricsMetadata } from 'types/track'

import type {
  AttachedFile,
  RequestOptions,
  GetGroupsParams,
} from './server/base'
import BaseAPI, { browserInstanceId } from './server/base'
import errorHandler, { HttpError } from './server/error-handler'
const contactUpdatePropsWhitelist: string[] = [
  'id',
  'name',
  'email',
  'phone',
  'company',
  'territory',
  'default_pro',
  'default_publisher',
  'pros',
  'publishers',
  'acts',
  'tags',
  'custom_data',
  'external_identifiers',
  'entity_details',
]
const maxTracksPerRequest: number = 200

type PlaylistEmail = {
  id: number
  recipients: Recipient[]
  message: string | null | undefined
  subject: string | null | undefined
  template: string | null | undefined
  manual: boolean | null | undefined
  territory_ids: GroupId[] | null | undefined
}

type TrackEmail = {
  id: number
  recipients: Recipient[]
  message: string | null | undefined
  subject: string | null | undefined
  template: string | null | undefined
  manual: boolean | null | undefined
  territory_ids: GroupId[] | null | undefined
}

const BULK_UPDATE_BATCH_SIZE = 100
export type BulkUpdateOptions = {
  name?: Track['name']
  album?: Track['album']
  artist?: Track['artist']
  track_id_for_artwork?: TrackId | null | undefined
  metadata?: Record<string, any>
  track_comments?: Array<Record<string, any>>
  track_writers?: Array<Record<string, any>>
  tags?: NativeTag['id'][]
  nesting_parent_id?: TrackId
}
type MFAAuthMethod = 'time_based'

type LyricTranscriptionResponse = {
  lyric_transcription_quota_left: number | null
  results: {
    [key: TrackId]: {
      success: boolean
      message: string
      lyrics?: string
      track_id?: TrackId
      track_name?: string
      metadata?: LyricsMetadata
    }
  }
}

type DeferredPlaylistReport = {
  deferred: boolean
  download_url: string | null
  additional_info: { results: number }
}

export type SavedGroup = {
  type: typeof RECIPIENT_GROUP
  id: number
  name: string
  created: string
  businesses: {
    id: number
    name: string
    url: string
    hostname: string
    image: string
    region: string
  }[]
  recipients: {
    id: number
    name: string
    email: string
    type: 'user'
    business_ids: number[]
    is_client: boolean
    group_id: number
    contact: Pick<Contact, 'id' | 'name' | 'email'>
  }[]
  sender_email: string
  sender_name: string
  url: string
}

export type UserSearchResult =
  | {
      type: typeof USER
      id: number
      email: string
      name: string
      is_client: boolean
      show_businesses: boolean
      businesses: Pick<BusinessData, 'id' | 'name'>[]
    }
  | SavedGroup
  | {
      type: typeof BUSINESS
      id: number
      name: string
    }
  | {
      type: typeof BRIEF
      id: number
      name: string
      recipients: {
        contact_id: number | null
        saved_recipients_id: RecipientListId | null
        user_id: number | null
        name: string
        email: string
      }[]
    }

export {
  serializeAggregatedTrack,
  serializeAggregatedSearchResults,
} from './utils/aggregated-search-track'
import {
  serializeAggregatedTrack,
  serializeAggregatedSearchResults,
} from './utils/aggregated-search-track'

function retry(
  maxRetries: number,
  fn: (...args: Array<any>) => any,
  signal?: AbortSignal,
) {
  if (signal?.aborted) return Promise.reject(new Error('Request aborted'))
  return new Promise<any>((resolve, reject) => {
    signal?.addEventListener('abort', () => {
      reject(new Error('Request aborted'))
    })

    const fnResult = _retry(maxRetries, fn)
      .then(result => resolve(result))
      .catch(err => reject(err))

    return fnResult
  })
}

const unrecoverableErrors = [500, 403]

function _retry(maxRetries: number, fn: () => Promise<any>) {
  return fn()
    .then(body => {
      if (body && body.error) throw new Error(body.error)
      return body
    })
    .catch(err => {
      if (maxRetries <= 0 || unrecoverableErrors.includes(err.status)) throw err
      return _retry(maxRetries - 1, fn)
    })
}

const canonicalObjectUrl = (url: string) =>
  url.replace(/#.*$/, '').replace('/cat/track/', '/track-new/')

export type NotesActivityLevel = 'all' | 'mentions_and_replies' | 'off'

export interface NotesNotificationPreference {
  activity_level: NotesActivityLevel
  channel_in_app: boolean
  channel_email: boolean
  auto_follow: boolean
}

let callQueue: Promise<any> = Promise.resolve() // class encapsulates api calls to server,
// all methods return promises with plain javascript objects

class Api extends BaseAPI {
  stagingEnv: boolean = __DEV__

  queueCall(call: (...args: Array<any>) => any): Promise<any> {
    callQueue = callQueue
      .catch(() => {
        // Resetting the callQueue state so as not to cache the error
        callQueue = Promise.resolve()
      })
      .then(call)
    return callQueue
  }

  switchEnvironment() {
    this.stagingEnv = !this.stagingEnv
  }

  // TODO: deprecated, use retryPromise instead
  retryCall(
    func: (...args: Array<any>) => any,
    signal?: AbortSignal,
  ): Promise<any> {
    return retry(4, func, signal)
  }

  // acquire new csrf token
  getCSRFToken(): Promise<string> {
    if (__REACT_NATIVE__) return Promise.resolve('')
    return this.getUrl<any>(
      `${this.host}/api/csrftoken/`,
      null,
      'api.getCSRFToken',
    ).then(result => {
      this.csrfToken = result
      return result
    })
  }

  getDiscoUpdate: () =>
    | Promise<{
        update: number
        target: string
      }>
    | null
    | undefined = () => {
    let url

    switch (getEnvName()) {
      case 'staging':
        url = 'https://static.disco-staging-2.com/disco-app/disco-update.json'
        break

      case 'prod':
        url = 'https://static.disco.ac/disco-app/disco-update.json'
        break

      default:
        return
    }

    return this.getUrl(url, null, 'api.getUpdate')
  }
  ping: () => Promise<void> = () =>
    this.getUrl(`${this.host}/ping`, null, 'api.ping')
  getConstanceConfig: () => Promise<{
    BRIEF_PROJECTS?: boolean
    BRIEF_WORKFLOW_V2?: boolean
  }> = () =>
    this.getUrl(
      `${this.host}/api/public_constance_config/`,
      null,
      'api.getConstanceConfig',
    )
  // user
  getUserDetails: () => Promise<any> = () =>
    this.getUrl(`${this.host}/api/user/me/`, null, 'api.getUserDetails')
  deleteUser: () => Promise<void> = () =>
    this.delUrl(`${this.host}/api/user/me/`, 'api.deleteUser')

  getNotesNotificationPreference: () => Promise<NotesNotificationPreference> =
    () =>
      this.getUrl(
        `${this.host}/api/notes-notification-preference/`,
        null,
        'api.getNotesNotificationPreference',
      )
  updateNotesNotificationPreference: (
    preference: Partial<NotesNotificationPreference>,
  ) => Promise<NotesNotificationPreference> = preference =>
    this.patchUrl(
      `${this.host}/api/notes-notification-preference/`,
      preference,
      'api.updateNotesNotificationPreference',
    )

  async updateBusinessSettings(
    settings: BusinessDataPatchRequest,
    progressEvent?: (percent: number) => any,
  ): Promise<any> {
    const { image, business_address, private_metadata, ...stringSettings } =
      settings

    if (private_metadata) {
      await this.postUrl(
        `${this.host}/api/business/current/update_metadata/`,
        private_metadata,
        'api.updateBusinessSettings',
      )
    }

    const additionalProps: RequestOptions = {
      fields: {},
      progressEvent: e => {
        if (progressEvent) progressEvent(e.percent)
      },
    }

    if (image === null) {
      additionalProps.fields = { ...additionalProps.fields, image: 'null' }
    } else if (image) {
      additionalProps.attachments = [
        {
          field: 'image',
          file: image,
          filename: image.name,
        },
      ]
    }

    if (business_address !== undefined) {
      additionalProps.fields = {
        ...additionalProps.fields,
        business_address: JSON.stringify(business_address),
      }
    }

    Object.keys(stringSettings).forEach((key: string) => {
      const value = stringSettings[key]

      if (value === null) {
        additionalProps.fields = { ...additionalProps.fields, [key]: '' }
      } else if (value !== undefined) {
        // Form data encoding requires all field values to be strings.
        // Boolean values must be explicitly converted (e.g., false -> "false")
        // to ensure they are included in the request body. Without this conversion,
        // boolean false values may be omitted or incorrectly serialized.
        // This applies to fields like auto_email_shared_playlist_passwords and
        // any other boolean fields added to BusinessDataPatchRequest.
        const fieldValue = typeof value === 'boolean' ? String(value) : value
        additionalProps.fields = {
          ...additionalProps.fields,
          [key]: fieldValue,
        }
      }
    })
    return this.patchUrl(
      `${this.host}/api/business/current/`,
      null,
      'api.updateBusinessSettings',
      additionalProps,
    )
  }

  updateUserSettings: (settings: Partial<Settings>) => Promise<any> = (
    settings: Partial<Settings>,
  ) =>
    this.patchUrl(
      `${this.host}/api/user/me/`,
      settings,
      'api.updateUserSettings',
    )
  sendFeedback: (arg0: { message: string }) => Promise<void> = ({ message }) =>
    this.postUrl(
      `${this.host}/api/redesign-feedback/`,
      {
        message,
      },
      'api.sendFeedback',
    )
  getPlaylistTags: () => Promise<[PlaylistTag]> = () =>
    this.getUrl(`${this.host}/api/playlist_tag/`, null, 'api.getPlaylistTags')

  createPlaylistTag(name: PlaylistTag['name']): Promise<PlaylistTag> {
    return this.postUrl(
      `${this.host}/api/playlist_tag/`,
      {
        name,
      },
      'api.createPlaylistTag',
    )
  }

  editPlaylistTag(tag: PlaylistTag): Promise<PlaylistTag> {
    return this.patchUrl(
      `${this.host}/api/playlist_tag/${tag.id}/`,
      tag,
      'api.editPlaylistTag',
    )
  }

  deletePlaylistTag(tagId: PlaylistTag['id']): Promise<void> {
    return this.delUrl(
      `${this.host}/api/playlist_tag/${tagId}/`,
      'api.deletePlaylistTag',
    )
  }

  // playlist types
  getPlaylistTypes: () => Promise<any> = () =>
    this.getUrl(`${this.host}/api/playlisttype/`, null, 'api.getPlaylistTypes')
  getWebsocketParams: (params: {
    retrieve_messages_after?: number | null | undefined
  }) => Promise<any> = (params: {
    retrieve_messages_after?: number | null | undefined
  }) =>
    this.postUrl(
      `${this.host}/api/websocket/`,
      params,
      'api.getWebsocketParams',
    )
  sendPasswordRecoveryInstructions: (
    email: string,
    returnUrl: string | null | undefined,
  ) => Promise<any> = (email: string, returnUrl: string | null | undefined) =>
    this.postUrl(
      `${this.host}/api/user/password_reset/`,
      {
        email: email,
        return: returnUrl,
      },
      'api.sendPasswordRecoveryInstructions',
    )
  // download
  getCustomDownloadUrl: (
    object_url: string,
    playlist_version: number | null | undefined,
    file_formats: TrackFormatSelection[],
    section_id?: string,
  ) => Promise<Record<string, any>> = (
    object_url,
    playlist_version,
    file_formats,
    section_id,
  ) => {
    return this.postUrl(
      `${this.host}/api/files_selection/`,
      {
        object_url: canonicalObjectUrl(object_url),
        playlist_version,
        file_formats,
        section_id,
      },
      'api.getCustomDownloadUrl',
    )
  }

  getBusinessesAnonymously(
    loginDetails: LoginDetails,
  ): Promise<Array<Record<string, any>>> {
    return this.postUrl(
      `${this.host}/api/add_to_disco/list_businesses/`,
      loginDetails,
      'api.getBusinessesAnonymously',
    )
  }

  getVisibleBusinesses(): Promise<
    Array<{ id: number; name: string; url: string }>
  > {
    // Literal, not the VISIBLE_BUSINESSES_PATH constant: @disco/utils depends on
    // @disco/api, so importing it back would be a circular dependency.
    return this.getUrl(
      `${this.host}/api-auth/businesses/`,
      null,
      'api.getVisibleBusinesses',
    )
  }

  addToDisco(
    objectUrl: string,
    extra: AddToDiscoParams,
    client_library_id?: ClientLibraryId | null | undefined,
  ): Promise<any> {
    // if trackFormatIds=undefined, whole playlist is added
    // TODO: validate businessUrl against scheme like this
    //       https?://[a-z\.\-](:[0-9]+)
    return this.postUrl(
      `${this.host}/api/add_to_disco/`,
      {
        object_url: canonicalObjectUrl(objectUrl),
        ...extra,
        client_library_id,
      },
      'api.addToDisco',
    )
  }

  saveToDiscoCustomer(token: string): Promise<any> {
    return this.postUrl(
      `${this.host}/api/widgets_api/save_to_disco_customer/begin_transfer/?token=${token}`,
      null,
      'api.saveToDiscoCustomer',
    )
  }

  getPlaylistFromPublicPage: (url: string) => Promise<any> = (url: string) => {
    return this.getUrl(`${this.host}/api/publicplaylist/`, {
      public_url: url,
    })
  }

  sendPlaylist(url: string, playlist: Playlist): Promise<void> {
    return this.postUrl(url, playlist)
  }

  searchForDuplicates: (
    info: Partial<TempTrack>,
    abortSignal?: AbortSignal,
  ) => Promise<any> = (info, abortSignal) =>
    this.getUrl(
      `${this.host}/api/search/`,
      {
        filename: info.filename,
        filters: JSON.stringify({
          Title: {
            value: info.title,
          },
          Artist: {
            value: info.artist,
          },
          Album: {
            value: info.album,
          },
        }),
        exact: '1',
        type: 'track',
      },
      'api.searchForDuplicates',
      {
        abortSignal,
      },
    )
  // upload
  uploadProviderFiles: (
    providerId: string,
    shortlistId: string,
    uploadId: string,
    files: TempItem[],
  ) => Promise<any> = (providerId, shortlistId, uploadId, tempItems) =>
    this.postUrl(`${this.host}/upload-api/external_upload/`, {
      provider: providerId,
      shortlist_id: shortlistId,
      upload_id: uploadId,
      files: tempItems.map(t => ({
        name: t.title,
        id: t.tempId,
        size: t.size,
        url: t.url,
        is_dir: false,
      })),
    })
  cancelProviderFileUpload: (externalUploadId: string) => Promise<void> = (
    externalUploadId: string,
  ) =>
    this.delUrl(
      `${this.host}/upload-api/external_upload/${externalUploadId}/`,
      'api.cancelProviderFileUpload',
    )

  getUploadPolicy(
    url: string | null | undefined,
    type: 'PRESIGNED_POST' | 'TEMPORARY_CREDENTIALS' = 'PRESIGNED_POST',
    abortSignal?: AbortSignal,
  ): Promise<any> {
    // workaround for safari caching request
    const timestamp = new Date().getTime()
    const randomToken = Math.round(Math.random() * 10000000)
    let timestampUrl = url
      ? `${url}${
          url.indexOf('?') === -1 ? '?' : '&'
        }timestamp=${timestamp}-${randomToken}`
      : null
    if (timestampUrl && !timestampUrl.includes(type))
      timestampUrl += `&policy_type=${type}`
    return this.getUrl(
      timestampUrl ||
        `${this.host}/upload-api/tracks/upload-policy-v2/?timestamp=${timestamp}-${randomToken}&policy_type=${type}`,
      null,
      'api.getUploadPolicy',
      {
        abortSignal,
      },
    )
  }

  uploadToS3(
    uploadPolicy: Record<string, any>,
    file: File,
    info: TempTrack,
    onUploadProgress: (info: TempTrack) => void,
    onUploadError: (info: TempTrack, err?: Error) => void,
    abortSignal?: AbortSignal,
  ): Promise<unknown> {
    return this.postUrl(uploadPolicy.url, null, 'api.uploadToS3', {
      skipAuth: true,
      fields: uploadPolicy.fields,
      attachments: [
        {
          field: 'file',
          file,
        },
      ],
      progressEvent: evt => {
        const xhr = evt.target
        const { readyState, status } = xhr

        if (evt.lengthComputable && onUploadProgress) {
          onUploadProgress({ ...info, progress: evt.percent })
        }

        if (
          readyState === 4 &&
          (status < 200 || status >= 400) &&
          onUploadError
        ) {
          onUploadError(info)
        }
      },
      abortSignal,
    })
  }

  getUploadResult(
    s3key: string,
    filename: string,
    url: string | null | undefined,
    transcode: boolean,
    filesize: number,
    abortSignal?: AbortSignal,
  ): Promise<any> {
    // TODO: remove this after testing

    /*
    if (Math.random() <= 0.5) {
      console.log('simulate api.getUploadResult failure:', url)
      return new Promise((resolve, reject) => {
        return reject(new Error('simulate failure'))
      })
    }
    */
    return this.postUrl(
      url || `${this.host}/upload-api/tracks/`,
      {
        s3_key: s3key,
        transcode,
        original_fn: filename,
        filesize,
      },
      'api.getUploadResult',
      {
        abortSignal,
      },
    )
  }

  // synchronization
  getExistingObjects(
    playlists: PlaylistId[] | null | undefined,
    playlistVersions: PlaylistVersionId[] | null | undefined,
    tracks: TrackId[],
    { includePlaylistsCount = false }: { includePlaylistsCount?: boolean } = {},
  ): Promise<{
    playlists: Record<PlaylistId, Playlist>
    tracks: Record<TrackId, Track>
  }> {
    const { host } = this
    const requestParams = __REACT_NATIVE__
      ? null
      : {
          // avoid passing X-Disco-ClientID on web as it will mangle url bounding to current session on backend
          skipClientIdentification: true,
        }

    // handle case where there are no tracks at all
    if (
      tracks.length === 0 &&
      ((playlists?.length ?? 0) > 0 || (playlistVersions?.length ?? 0) > 0)
    ) {
      const query = {
        playlist_ids: playlists ? playlists : [],
        playlistversion_ids: playlistVersions ? playlistVersions : [],
      }
      return this.postUrl(
        `${host}/api/refresh/`,
        query,
        'api.getExistingObjects',
        requestParams,
      )
    }

    if (tracks.length > 0) {
      // split tracks into chunks and call backend serially to reduce load
      // on server and evade loading failures due to server timeouts
      const trackChunks: TrackId[][] = []

      for (let i = 0; i < tracks.length; i += maxTracksPerRequest) {
        trackChunks.push(tracks.slice(i, i + maxTracksPerRequest))
      }

      return Promise.all([
        (playlists?.length ?? 0) > 0 || (playlistVersions?.length ?? 0) > 0
          ? this.postUrl(
              `${host}/api/refresh/`,
              {
                playlist_ids: playlists || [],
                playlistversion_ids: playlistVersions || [],
              },
              'api.getExistingObjects',
              requestParams,
            )
          : null,
        mapSeries(trackChunks, chunk => {
          // execute serially to not hit backend too hard
          return this.postUrl(
            `${host}/api/refresh/`,
            {
              track_ids: chunk,
              ...(includePlaylistsCount && { include_playlists_count: true }),
            },
            'api.getExistingObjects',
            requestParams,
          )
        }),
      ]).then(([resultPlaylists, resultTracks]) => {
        const result = {
          tracks: {},
          playlists: {},
        }
        merge(result, resultPlaylists, ...resultTracks)
        return result
      })
    }

    return Promise.resolve({
      tracks: {},
      playlists: {},
    })
  }

  getNotifications: () => Promise<any> = () =>
    this.getUrl(
      `${this.host}/api/notification/?read=0`,
      null,
      'api.getNotifications',
    )
  dismissNotification: (
    notification: DismissibleNotification,
  ) => Promise<void> = (notification: DismissibleNotification) =>
    this.putUrl(
      notification.api_url,
      { ...notification, is_read: true },
      'api.dismissNotification',
    )
  // - Marks all notifications as read
  //
  // - Marks all notifications from group as read [DEPRECATED]
  //
  //     {"group_ids":[1,2,3]}
  //
  dismissAllNotifications: (group_ids?: Array<string>) => Promise<any> =
    group_ids =>
      this.postUrl(
        `${this.host}/api/notification/mark_all_read/`,
        {
          group_ids,
        },
        'api.dismissAllNotifications',
      )

  // referral program
  emailReferralInvitation(emails: string[], message: string): Promise<any> {
    return this.postUrl(
      `${this.host}/api/email_referral_invitation/`,
      {
        emails,
        message,
      },
      'api.emailReferralInvitation',
    )
  }

  getPlaylistShares(playlistId: number): Promise<any> {
    return this.getUrl(
      `${this.host}/api/playlist/${playlistId}/share/`,
      null,
      'api.getShares',
    )
  }

  getTrackShares(trackId: number): Promise<any> {
    return this.getUrl(
      `${this.host}/api/track/${trackId}/share/`,
      null,
      'api.getTrackShares',
    )
  }

  disableShare(shareId: number): Promise<void> {
    // disableShare has optional body, which isn't needed for UI:
    //
    //     { recipients_ids: [group_id1, group_id2] }
    //
    return this.postUrl(
      `${this.host}/api/share/${shareId}/disable/`,
      null,
      'api.disableShare',
    )
  }

  enablePlaylistShare(shareId: number): Promise<void> {
    return this.postUrl(
      `${this.host}/api/share/${shareId}/enable/`,
      null,
      'api.enablePlaylistShare',
    )
  }

  shorturl(longUrls: Array<string>): Promise<string[]> {
    const items = longUrls.map(url => ({
      url,
    }))
    return this.postUrl<any>(
      `${this.host}/api/shorturl/`,
      items,
      'shorten',
    ).then(result => result.map(item => item.short_url))
  }

  playlistImplicitShare(playlist_id: PlaylistId): Promise<any> {
    return this.postUrl(
      `${this.host}/api/playlist_implicit_share/`,
      {
        playlist_id,
      },
      'api.playlistImplicitShare',
    )
  }

  // embeds
  getEmbeddedPlaylistUrl(
    playlistId: number,
    options: Record<string, any>,
  ): Promise<string> {
    return this.getUrl(
      `${this.host}/api/playlist/${playlistId}/embed_url/`,
      options,
      'api.getEmbeddedPlaylistUrl',
    )
  }

  getEmbeddedTrackUrl(
    trackId: number,
    options: Record<string, any>,
  ): Promise<string> {
    return this.getUrl(
      `${this.host}/api/track/${trackId}/embed_url/`,
      options,
      'api.getEmbeddedTrackUrl',
    )
  }

  // playlist sending
  emailPlaylist(email: PlaylistEmail): Promise<any> {
    const { id, ...rest } = email
    return this.postUrl(
      `${this.host}/api/playlist/${id}/share/`,
      { ...rest },
      'api.emailPlaylist',
    )
  }

  emailTrack(email: TrackEmail): Promise<any> {
    const { id, ...rest } = email

    return this.postUrl(
      `${this.host}/api/track/${id}/share/`,
      { ...rest },
      'api.emailTrack',
    )
  }

  // campaigns
  getCampaigns(): Promise<{
    results: EmailCampaign[]
  }> {
    return this.getUrl(`${this.host}/api/campaign/`, null, 'api.getCampaigns')
  }

  getCampaign(id: EmailCampaignId): Promise<EmailCampaign> {
    return this.getUrl(
      `${this.host}/api/campaign/${id}/`,
      null,
      'api.getCampaign',
    )
  }

  getCampaignStats(
    id: EmailCampaignId,
    fullStats: boolean,
  ): Promise<EmailCampaignStats> {
    const params = fullStats
      ? {
          full: 1,
        }
      : null
    return this.getUrl(
      `${this.host}/api/campaign/${id}/stats/`,
      params,
      'api.getCampaignStats',
    )
  }

  createCampaign(campaign: Partial<EmailCampaign>): Promise<EmailCampaign> {
    return this.postUrl(
      `${this.host}/api/campaign/`,
      campaign,
      'api.createCampaigns',
    )
  }

  updateCampaign(
    campaignId: number,
    campaign: EmailCampaign,
  ): Promise<EmailCampaign> {
    return this.patchUrl(
      `${this.host}/api/campaign/${campaignId}/`,
      campaign,
      'api.updateCampaign',
    )
  }

  sendTestCampaign(
    campaignId: number,
    recipientEmails: string[],
    mockData: any,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/campaign/${campaignId}/test_send/`,
      {
        emails: recipientEmails,
        mock_data: mockData,
      },
      'api.sendTestCampaign',
    )
  }

  sendCampaign(campaignId: number): Promise<any> {
    return this.postUrl(
      `${this.host}/api/campaign/${campaignId}/send/`,
      null,
      'api.sendCampaign',
    )
  }

  validateDomain(emailDomain: string): Promise<any> {
    return this.postUrl(
      `${this.host}/api/campaign/validate_domain/`,
      {
        domain_address: emailDomain,
      },
      'api.validateDomain',
    )
  }

  deleteCampaign(campaignId: number): Promise<void> {
    return this.delUrl(
      `${this.host}/api/campaign/${campaignId}/`,
      'api.deleteCampaign',
    )
  }

  uploadEmailImage(imageFile: File, abortSignal?: AbortSignal): Promise<any> {
    return this.postUrl(
      `${this.host}/api/uploaded_image/`,
      null,
      'api.uploadEmailImage',
      {
        fields: {
          target: 'campaign',
        },
        attachments: [
          {
            field: 'image',
            file: imageFile,
            filename: imageFile.name,
          },
        ],
        abortSignal,
      },
    )
  }

  getCampaignFooters(): Promise<any> {
    return this.getUrl(
      `${this.host}/api/campaign_footers/`,
      null,
      'api.getCampaignFooters',
    )
  }

  createCampaignFooter(footer: Record<string, any>): Promise<any> {
    return this.postUrl(
      `${this.host}/api/campaign_footers/`,
      footer,
      'api.createCampaignFooter',
    )
  }

  updateCampaignFooter(
    footerId: number,
    footer: Record<string, any>,
  ): Promise<any> {
    return this.putUrl(
      `${this.host}/api/campaign_footers/${footerId}/`,
      footer,
      'api.updateCampaignFooter',
    )
  }

  deleteCampaignFooter(footerId: number): Promise<void> {
    return this.delUrl(
      `${this.host}/api/campaign_footers/${footerId}/`,
      'api.deleteCampaignFooter',
    )
  }

  // PUBLIC API unsubscribe
  campaignUnsubscribe(
    em: string,
    cp: string | null | undefined,
    sr: string | null | undefined,
    resubscribe: boolean,
    reason?: string,
  ): Promise<void> {
    return this.postUrl(
      `${this.host}/api/unsubscribe/`,
      {
        em: em,
        cp: cp,
        sr: sr,
        resubscribe: resubscribe ? 1 : undefined,
        reason: reason || null,
      },
      'api.campaignUnsubscribe',
    )
  }

  campaignCheckUnsubscribe(em?: string): Promise<any> {
    return this.postUrl(
      `${this.host}/api/unsubscribe/hello/`,
      {
        em: em,
      },
      'api.campaignCheckUnsubscribe',
    )
  }

  /* -------------------- */
  async postUserAuth(loginDetails: LoginDetails): Promise<any> {
    if (__REACT_NATIVE__) {
      return this.postUrl(
        `${this.host}/api-auth/token-auth/`,
        loginDetails,
        'api.postUserAuthJWT',
      )
    }

    if ('saml' in loginDetails) {
      return this.postUrl(
        `${this.host}/api-auth/saml-login-welcome/`,
        loginDetails,
        'api.postUserAuthSAML',
      )
    }

    // Referer-based flows (password reset, invites, welcome) authenticate via a
    // signed referer URL, not username/password. The global-login endpoint only
    // supports username/password, so keep these on /api-auth/login/ even on the
    // accounts.* subdomain. See _referer_login in leveltwo-disco login.py.
    const isRefererFlow = 'referer' in loginDetails

    if (isLoggingInFromAccountsSubdomain() && !isRefererFlow) {
      const resp = await this.postUrl<{
        extra_data?: {
          available_businesses?: Array<{
            hostname: string
            last_access: string
          }>
        }
        reason: string
        redirect_to?: string // redirect_to - client-side property (but could be refactor to server-side)
      }>(
        `${this.host}/api-auth/global-login/`,
        { ...loginDetails, accounts_auth: 1 },
        'api.postUserAuth',
      )

      if (resp?.extra_data?.available_businesses?.length) {
        const lastAccessedBusiness = resp.extra_data.available_businesses.sort(
          (a, b) =>
            new Date(b.last_access).getTime() -
            new Date(a.last_access).getTime(),
        )[0]
        if (lastAccessedBusiness) {
          // add redirect_to to response to perform redirect later
          resp.redirect_to = `/api-auth/transfer-start/?business_hostname=${lastAccessedBusiness.hostname}`
        }
      }
      return resp
    }
    return this.postUrl(
      `${this.host}/api-auth/login/`,
      loginDetails,
      'api.postUserAuth',
    )
  }

  samlLogin({
    username,
    redirect_uri,
  }: {
    username: string
    redirect_uri: string
  }): Promise<any> {
    return this.postUrl(
      `${this.host}/api-auth/saml-login/?redirect_uri=${encodeURIComponent(
        redirect_uri,
      )}`,
      {
        username,
      },
      'api.postUserAuth',
      {
        skipAuth: true,
      },
    )
  }

  multiAccountLogin(loginDetails: LoginDetails): Promise<any> {
    return this.postUrl(
      this.stagingEnv
        ? `https://auth.disco-staging-2.com/api-auth/global-login/`
        : `https://auth.disco.ac/api-auth/global-login/`,
      { ...loginDetails, is_jwt_auth: true },
      'api.postUserAuth',
      {
        skipAuth: true,
      },
    )
  }

  fetchUserMemberships(useThisHost?: boolean): Promise<any> {
    let url = this.stagingEnv
      ? `https://auth.disco-staging-2.com/api/business/membership/`
      : `https://auth.disco.ac/api/business/membership/`

    if (useThisHost) {
      url = `${this.host}/api/business/membership/`
    }

    return this.getUrl(url, null, 'api.fetchUserMemberships')
  }

  mobileSendPasswordRecoveryInstructions(email: string): Promise<any> {
    return this.postUrl(
      this.stagingEnv
        ? `https://auth.disco-staging-2.com/api/user/password_reset/`
        : `https://auth.disco.ac/api/user/password_reset/`,
      {
        email: email,
      },
      'api.sendPasswordRecoveryInstructions',
    )
  }

  checkLoginUrl(referrerUrl: string): Promise<void> {
    return this.postUrl(
      `${this.host}/api-auth/check-login-url/`,
      {
        referer: referrerUrl,
      },
      'api.checkLoginUrl',
    )
  }

  changePassword(
    newPassword: string,
    oldPassword: string | null | undefined,
  ): Promise<any> {
    return this.patchUrl(
      `${this.host}/api/user/me/`,
      {
        new_password: newPassword,
        old_password: oldPassword,
      },
      'api.changePassword',
    )
  }

  getUserLogout: () => Promise<void> = () => {
    this.jwtToken = null
    return this.postUrl(
      `${this.host}/api-auth/logout/`,
      {},
      'api.getUserLogout',
    ).then(() => {
      this.csrfToken = null
    })
  }

  postUserLogoutAll: () => Promise<void> = () => {
    return this.postUrl(
      `${this.host}/api-auth/logout/`,
      {
        logout_all: true,
      },
      'api.postUserLogoutAll',
    )
  }

  // search
  getSearchResults(
    type: 'share',
    searchTerm: string,
    options: {
      includeBlankEmails: boolean | null | undefined
      hideBusinesses: boolean | null | undefined
      includeBriefs?: boolean | null | undefined
    },
    abortSignal?: AbortSignal,
  ): Promise<Array<UserSearchResult>> {
    let { includeBlankEmails, hideBusinesses, includeBriefs } = options
    if (!includeBlankEmails) includeBlankEmails = undefined
    if (!hideBusinesses) hideBusinesses = undefined
    if (!includeBriefs) includeBriefs = undefined
    return this.getUrl<any>(
      `${this.host}/api/user_search/`,
      {
        q: searchTerm,
        type: type,
        include_blank_emails: includeBlankEmails,
        hide_businesses: hideBusinesses,
        include_briefs: includeBriefs,
      },
      'api.getSearchResults',
      {
        abortSignal,
      },
    ).then(response => {
      if (type !== 'share') return response
      // normalize response
      const result: any[] = []
      response.forEach(item => {
        if (item.type !== 'saved') result.push(item)
        const { businesses, recipients } = item

        if (businesses && recipients && businesses.length > 0) {
          const normalizedRecipients = recipients.map(recipient => {
            if (recipient.business_ids && recipient.business_ids.length > 0) {
              const recipientBusinesses = businesses.filter(business => {
                return recipient.business_ids.indexOf(business.id) > -1
              })
              return { ...recipient, businesses: recipientBusinesses }
            }

            return recipient
          })
          result.push({ ...item, recipients: normalizedRecipients })
        }
      })
      return result
    })
  }

  getSearchResultsByQuery(
    groups: number[] | null | undefined,
    query: Record<string, any>,
    aggregated_search?: boolean,
    abortSignal?: AbortSignal,
  ): Promise<any> {
    if (query.simSearch) {
      return this.getSimilarTracks(
        null,
        query.simSearch.url,
        aggregated_search,
        abortSignal,
      )
    }

    return this.getUrl(
      `${this.host}/api/search/`,
      query,
      'api.getSearchResultsByQuery',
      {
        headers: {
          Accept: 'application/json',
          ...(groups
            ? {
                'X-Disco-GroupIDs': JSON.stringify(groups),
              }
            : {}),
        },
        abortSignal,
      },
    )
  }

  // Similarity search link preview
  getLinkInfo: (url: string) => Promise<{
    duration: number
    id: string
    thumbnail: string
    title: string
    type: string
  }> = (url: string) =>
    this.postUrl(
      `${this.host}/api/search/link_info/`,
      { url },
      'api.getLinkInfo',
    )

  // V2 Search API
  searchV2(
    payload: V2SearchQuery,
    abortSignal?: AbortSignal,
  ): Promise<{
    hits: any[]
    total: number
    pagination: {
      cursor: string | null
      has_more: boolean
    }
    context?: {
      title: string
      waveform_url: string
    }
  }> {
    const v2Payload: V2SearchQuery = {
      result_types: ['track'],
      ...payload,
    }

    return this.postUrl(
      `${this.host}/api/v2/search/`,
      v2Payload,
      'api.searchV2',
      {
        abortSignal,
        skipClientIdentification: true,
      },
    )
  }

  // aggregated search
  getAggregatedTrackByUrl(url: string): Promise<Track> {
    return this.getUrl(url, null, 'api.getAggregatedTrackByUrl').then(
      serializeAggregatedTrack,
    )
  }

  getAggregatedSearchTrack(
    track_id: TrackId,
    client_library_id: number,
  ): Promise<Track> {
    return this.getUrl<any>(
      `/api/aggregated_search/client_library/${client_library_id}/track/${track_id}/`,
      null,
      'api.getAggregatedSearchTrack',
    ).catch(() => {
      throw new Error('Track not found')
    })
  }

  getAggregatedSearchBatchTracks(
    track_ids: {
      track_id: TrackId
      client_library_id: number
    }[],
  ): Promise<{
    results: Track[]
  }> {
    return this.postUrl<any>(
      '/api/aggregated_search/batch_tracks/',
      {
        track_ids,
      },
      'api.getAggregatedSearchBatchTracks',
      {
        // avoid passing X-Disco-ClientID on web as it will mangle url bounding to current session on backend
        // TODO: refactor? https://disco-team.atlassian.net/browse/DISCO-6308
        skipClientIdentification: true,
      },
    ).then(results => ({
      results: serializeAggregatedSearchResults(results),
    }))
  }

  // metadata
  getMetadataKeys: () => Promise<any> = () =>
    this.getUrl(`${this.host}/api/metadata-keys/`, null, 'api.getMetadataKeys')

  // playlists
  savePlaylist(playlist: Shortlist): Promise<Playlist> {
    return this.postUrl(
      `${this.host}/api/playlistversion/`,
      playlist,
      'api.savePlaylist',
    )
  }

  // createPlaylist essentially does the same thing as savePlaylist above,
  // but does not have any dependency on Shortlist type - which is what mobile wants
  createPlaylist(playlist: {
    name: string
    track_ids: number[]
    group_ids: number[]
    is_client_version: boolean
    playlist_id?: number
    sections?: Array<{ id: string; name: string; position: number }>
    album_image_url?: string | null
    tracks_album_image?: string | null
  }): Promise<Playlist> {
    return this.postUrl(
      `${this.host}/api/playlistversion/`,
      playlist,
      'api.createPlaylist',
    )
  }

  getPlaylistBaseObj(playlistId: number): Promise<any> {
    return this.getUrl(
      `${this.host}/api/playlist/${playlistId}/`,
      null,
      'api.getPlaylistBaseObj',
    )
  }

  updatePlaylistBaseObj(
    playlistId: PlaylistId,
    changes: Record<string, any>,
  ): Promise<any> {
    return this.patchUrl(
      `${this.host}/api/playlist/${playlistId}/`,
      changes,
      'api.updatePlaylistBaseObj',
    )
  }

  updatePlaylist(
    playlistId: PlaylistId,
    data: Record<string, any>,
    progressEvent?: (percent: number) => unknown,
  ): Promise<any> {
    const { image, ...stringSettings } = data
    const additionalProps: RequestOptions = {
      fields: {},
      progressEvent: e => {
        if (progressEvent) progressEvent(e.percent)
      },
    }

    if (image === null) {
      additionalProps.fields = { ...additionalProps.fields, image: '' }
    } else if (image) {
      additionalProps.attachments = [
        {
          field: 'image',
          file: image,
          filename: image.name,
        },
      ]
    }

    Object.keys(stringSettings).forEach((key: string) => {
      const value = stringSettings[key]

      if (value === null) {
        additionalProps.fields = { ...additionalProps.fields, [key]: '' }
      } else if (value !== undefined) {
        additionalProps.fields = { ...additionalProps.fields, [key]: value }
      }
    })
    return this.patchUrl(
      `${this.host}/api/playlist/${playlistId}/`,
      null,
      'api.updatePlaylist',
      additionalProps,
    )
  }

  batchUpdatePlaylists(
    playlist_ids: PlaylistId[],
    data: Partial<Playlist>,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/playlist/batch_update/`,
      {
        playlist_ids,
        data,
      },
      'api.batchUpdatePlaylists',
    )
  }

  searchPlaylistsWithTrackArtworks(
    query: string,
    playlist_type?: Playlist['playlist_type'],
  ): Promise<any> {
    return this.getUrl(
      `${this.host}/api/search/`,
      {
        playlist_type,
        q: query,
        return_tracks_artworks: 1,
        type: 'playlist',
      },
      'api.searchPlaylists',
    )
  }

  saveArtworkAlias: (id: number, artwork: File) => Promise<unknown> = (
    id: number,
    artwork: File,
  ) => {
    return this.putUrl(
      `${this.host}/api/playlistversion/${id}/artwork_alias/`,
      null,
      'api.saveArtworkAlias',
      {
        attachments: [
          {
            field: 'artwork',
            file: artwork,
            filename: artwork.name,
          },
        ],
      },
    )
  }

  changePlaylistTheme(playlist: Playlist, theme: PlaylistTheme): Promise<any> {
    return this.patchUrl(
      `${this.host}/api/playlistversion/${playlist.id}/`,
      {
        theme: theme ? theme.url : null,
      },
      'api.changePlaylistTheme',
    )
  }

  deletePlaylistVersion: (id: PlaylistVersionId) => Promise<void> = id =>
    this.delUrl(
      `${this.host}/api/playlistversion/${id}/`,
      'api.deletePlaylistVersion',
    )
  getPlaylistZipUrls: (arg0: {
    playlist_zip_urls?: string
  }) => Promise<ZipUrls> = ({
    playlist_zip_urls,
  }: {
    playlist_zip_urls?: string
  }) => {
    if (!playlist_zip_urls) return Promise.resolve({})
    return this.getUrl(playlist_zip_urls, null, 'api.getPlaylistZipUrls')
  }

  // tracks
  alterTrackGroups(
    tracks: Track[],
    addGroups: Group[],
    removeGroups: Group[] = [],
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/track/alter_groups/`,
      {
        track_ids: tracks.map(track => track.id),
        add_group_ids: addGroups.map(group => group.id),
        remove_group_ids: removeGroups.map(group => group.id),
      },
      'api.alterTrackGroups',
    )
  }

  importInboxTracks(
    tracks: Track[],
    addGroups: Group[],
    removeGroups: Group[] = [],
    includeStems?: boolean,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/track/inbox_import/`,
      {
        track_ids: tracks.map(track => track.id),
        add_group_ids: addGroups.map(group => group.id),
        remove_group_ids: removeGroups.map(group => group.id),
        include_stems: includeStems,
      },
      'api.importInboxTracks',
    )
  }

  alterPlaylistGroups(
    playlists: Playlist[],
    addGroups: Group[],
    removeGroups: Group[] = [],
    alterTracks: boolean = false,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/playlist/alter_groups/`,
      {
        playlist_ids: playlists.map(playlist => playlist.playlist_id),
        add_group_ids: addGroups.map(group => group.id),
        remove_group_ids: removeGroups.map(group => group.id),
        alter_tracks: alterTracks ? alterTracks : undefined,
      },
      'api.alterPlaylistGroups',
    )
  }

  importInboxPlaylists(
    playlists: Playlist[],
    addGroups: Group[],
    removeGroups: Group[] = [],
    alterTracks: boolean = false,
    includeStems?: boolean,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/playlist/inbox_import/`,
      {
        playlist_ids: playlists.map(playlist => playlist.playlist_id),
        add_group_ids: addGroups.map(group => group.id),
        remove_group_ids: removeGroups.map(group => group.id),
        alter_tracks: alterTracks ? alterTracks : undefined,
        include_stems: includeStems,
      },
      'api.importInboxPlaylists',
    )
  }

  removeTracksFromBrowse(track_ids: TrackId[]): Promise<void> {
    return this.delUrl(
      `${this.host}/api/track/groups/`,
      'api.removeTracksFromBrowse',
      {
        track_ids,
      },
    )
  }

  removePlaylistsFromBrowse(playlist_ids: PlaylistId[]): Promise<void> {
    return this.delUrl(
      `${this.host}/api/playlist/groups/`,
      'api.removePlaylistsFromBrowse',
      {
        playlist_ids,
      },
    )
  }

  mergeTracks(
    masterTrack?: SelectableTrack,
    tracksToMerge?: SelectableTrack[],
    losslessFormatTrack?: SelectableTrack,
  ): Promise<unknown> {
    const { host } = this
    return this.postUrl(
      `${host}/api/track/${masterTrack?.id || ''}/merge/`,
      {
        track_ids: tracksToMerge?.map(t => t.id),
        lossless_id: losslessFormatTrack ? losslessFormatTrack.id : undefined,
      },
      'api.mergeTracks',
    )
  }

  getTrack(trackId: TrackId): Promise<Track> {
    return this.getUrl(
      `${this.host}/api/track/${trackId}/`,
      null,
      'api.getTrack',
    )
  }

  // Playlists a track appears in, for the search "Appears on" hover card.
  getTrackPlaylists(trackId: TrackId): Promise<TrackPlaylistsResponse> {
    return this.getUrl(
      `${this.host}/api/track/playlists/`,
      { track_ids: String(trackId) },
      'api.getTrackPlaylists',
    )
  }

  getNestedTracks(trackId: TrackId, label?: NestingLabel): Promise<any> {
    return this.getUrl(
      `${this.host}/api/track/${trackId}/nested_tracks/`,
      {
        label,
      },
      'api.getNestedTracks',
    )
  }

  getDownloadableTrackMetadata(trackId: TrackId): Promise<any> {
    return this.getUrl(
      `${this.host}/api/track/${trackId}/downloadable_metadata/`,
      null,
      'api.getDownloadableTrackMetadata',
    )
  }

  /**
   * Create audio separation for tracks.
   * Despite the method name (kept for backwards compatibility), this supports both:
   * - 'instrumental': Removes vocals, keeps music (Instant Instrumental)
   * - 'vocals': Removes music, keeps vocals (Instant A Cappella)
   */
  createInstantInstrumental(
    track_ids: TrackId[],
    output_type: 'instrumental' | 'vocals' = 'instrumental',
  ): Promise<
    Record<TrackId, { success: boolean; message?: string; track_id?: TrackId }>
  > {
    return this.postUrl(
      `${this.host}/api/instant_instrumental/`,
      {
        track_ids,
        output_type,
      },
      'api.createInstantInstrumental',
    )
  }

  pendingUploadPromises: Record<
    string,
    {
      resolve: (result: Shortlist) => void
      reject: (error: Error) => void
    }
  > = {}
  similarTracksRequests: object = {}
  aggregatedSimilarTracksRequests: object = {}

  getSimilarTracks(
    track_id: TrackId | null | undefined,
    url: string | null | undefined,
    aggregated_search?: boolean,
    abortSignal?: AbortSignal,
  ): Promise<any> {
    const request_id = cuid()
    const trackId = aggregated_search ? null : track_id
    return this.postUrl(
      `${this.host}/api/similarity_search/tracks/`,
      {
        track_id: trackId || undefined,
        url: url || undefined,
        request_id,
        aggregated_search,
      },
      'api.getSimilarTracks',
      {
        abortSignal,
      },
    ).then(() =>
      new Promise((resolve, reject) => {
        if (aggregated_search) {
          this.aggregatedSimilarTracksRequests[request_id] = {
            resolve,
            reject,
          }
        } else {
          this.similarTracksRequests[request_id] = {
            resolve,
            reject,
          }
        }

        setTimeout(
          () => reject(new Error('Similar tracks request timed out')),
          120000,
        ) // timeout to avoid leaks
      }).finally(() => {
        delete this.similarTracksRequests[request_id]
        delete this.aggregatedSimilarTracksRequests[request_id]
      }),
    )
  }

  getTrackWaveform(trackId: number): Promise<{ samples?: number[] }> {
    return this.getUrl<{ samples?: number[]; error?: string }>(
      `${this.host}/api/track/${trackId}/waveform/`,
      null,
      'api.getTrackWaveform',
    ).then(result => {
      const { error } = result

      if (error) {
        throw new Error(error)
      } else {
        return result
      }
    })
  }

  diagnoseLibraryMembership(libraryId: number, trackId: TrackId): Promise<any> {
    return this.getUrl(
      `${this.host}/api/client_library/${libraryId}/track/${trackId}/diagnose_library_membership/`,
      null,
      'api.diagnoseLibraryMembership',
    )
  }

  sendEmbedPlayEvent: () => void = () => {
    Storage.set('embed_playback', browserInstanceId)
    window.parent.postMessage(
      `disco_play_track, source: ${browserInstanceId}`,
      '*',
    )
  }

  sendTrackEvent(
    type: 'play_track' | 'download_track' | 'access_track',
    trackId: number,
    eventSource: EventSource,
    options: TrackEventOptions,
  ): Promise<void> {
    const { host } = this
    let query = ''
    const clid = options.client_library_id

    if (clid) {
      query += `?client_library_id=${clid}`
    }

    if (eventSource === 'embed' && type === 'play_track' && window.parent) {
      this.sendEmbedPlayEvent()
    }

    return this.postUrl<void>(
      `${host}/api/track/${trackId}/event/` + query,
      {
        event_name: type,
        ...omitBy(options, v => v === null || v === undefined),
        event_source: eventSource,
        user_id: window.user_id,
      },
      'api.sendTrackEvent',
    ).catch(err => {
      errorHandler(err, 'api.sendTrackEvent')
    })
  }

  transcribeTracks(trackIds: TrackId[]): Promise<LyricTranscriptionResponse> {
    return this.postUrl(
      `${this.host}/api/lyric_transcription/?response_v2=true`,
      {
        track_ids: trackIds,
      },
      'api.transcribeTracks',
    )
  }

  sendTranscriptionFeedback(
    trackId: TrackId,
    feedback: 'positive' | 'negative',
    reason?: string,
    otherReason?: string,
  ): Promise<void> {
    return this.postUrl(
      `${this.host}/api/lyric_transcription/feedback/`,
      {
        track_id: trackId,
        feedback,
        reason,
        other_reason: otherReason,
      },
      'api.sendTranscriptionFeedback',
    )
  }

  sendTrackPlaybackEvent(
    trackId: number,
    eventSource: EventSource,
    options: TrackEventOptions,
  ) {
    this.sendTrackEvent('play_track', trackId, eventSource, options)
  }

  sendTrackDownloadEvent(
    trackId: number,
    eventSource: EventSource,
    options: TrackEventOptions,
  ) {
    this.sendTrackEvent('download_track', trackId, eventSource, options)
  }

  sendTrackAccessEvent(
    trackId: number,
    eventSource: EventSource,
    options: TrackEventOptions,
  ) {
    this.sendTrackEvent('access_track', trackId, eventSource, options)
  }

  sendPlaylistEvent(
    event_name: 'access_playlist' | 'download_zip',
    playlistId: number,
    share_id?: number | null | undefined,
    group_id?: number | null | undefined,
    campaign_id?: number | null | undefined,
    recipient_id?: number | null | undefined,
    client_library_id?: number | null | undefined,
  ): Promise<void> {
    const { host } = this
    const qs = client_library_id
      ? `?client_library_id=${client_library_id}`
      : ''
    return this.postUrl<any>(
      `${host}/api/playlist/${playlistId}/event/${qs}`,
      {
        event_name,
        share_id,
        group_id,
        campaign_id,
        recipient_id,
        user_id: window.user_id,
      },
      'api.sendPlaylistEvent',
    ).catch(err => {
      errorHandler(err, 'api.sendPlaylistEvent')
    })
  }

  sendPlaylistAccessEvent: (
    playlistId: number,
    shareId: number | null | undefined,
    groupId: number | null | undefined,
    clientLibraryId?: number | null | undefined,
  ) => Promise<void> = (playlistId, shareId, groupId, clientLibraryId) =>
    this.sendPlaylistEvent(
      'access_playlist',
      playlistId,
      shareId,
      groupId,
      undefined,
      undefined,
      clientLibraryId,
    )
  sendPlaylistDownloadEvent: (
    playlistId: number,
    shareId: number | null | undefined,
    groupId: number | null | undefined,
    campaignId: number | null | undefined,
    recipientId: number | null | undefined,
    clientLibraryId: number | null | undefined,
  ) => Promise<void> = (
    playlistId,
    shareId,
    groupId,
    campaignId,
    recipientId,
    clientLibraryId,
  ) =>
    this.sendPlaylistEvent(
      'download_zip',
      playlistId,
      shareId,
      groupId,
      campaignId,
      recipientId,
      clientLibraryId,
    )

  getTrackStats(trackId: number): Promise<any> {
    return this.getUrl(
      `${this.host}/api/v2/customer_stats/listener_stats_for_track/?track_id=${trackId}`,
      null,
      'api.getTrackStats',
    )
  }

  updateTrackMetadata(
    trackId: TrackId,
    newMetadata: Record<string, any>,
  ): Promise<any> {
    const requestParams = __REACT_NATIVE__
      ? undefined
      : {
          // avoid passing X-Disco-ClientID on web
          skipClientIdentification: true,
        }
    return this.putUrl(
      `${this.host}/api/track/${trackId}/metadata/`,
      newMetadata,
      'updateTrackMetadata',
      requestParams,
    )
  }

  updatePlaylistMetadata(
    playlist: Playlist,
    metadata: Partial<Playlist>,
  ): Promise<any> {
    return this.patchUrl(
      `${this.host}/api/playlistversion/${playlist.id}/`,
      metadata,
      'api.updatePlaylistMetadata',
    )
  }

  getPlaylistPage(url: string): Promise<any> {
    return this.getUrl(url, null, 'api.getPlaylistPage')
  }

  getPlaylist(
    playlist: Record<string, any>,
    options: {
      pageSize: number
      territories?: GroupId[]
      includeNestedTracks?: boolean
      share?: boolean
    },
    actionInfo?: string,
  ): Promise<any> {
    const { versions, url } = playlist
    let loadUrl = versions ? versions[0] : url
    loadUrl += `?tracks_page_size=${options.pageSize}`
    loadUrl += options.territories
      ? `&territory_ids=${JSON.stringify(options.territories)}`
      : ''
    loadUrl += options.includeNestedTracks ? `&include_nested_tracks=1` : ''
    // Shared/inbox playlists require ?share=1 for the recipient to load the version.
    loadUrl += options.share ? `&share=1` : ''
    return this.getUrl(loadUrl, null, actionInfo)
  }

  getFullPlaylist(playlist: Playlist, territories?: GroupId[]): Promise<any> {
    return this.getPlaylist(
      playlist,
      {
        pageSize: 10000,
        territories,
        includeNestedTracks: true,
      },
      'api.getFullPlaylist',
    )
  }

  getPublicPlaylistByPlaylistId(playlist: Record<string, any>): Promise<any> {
    const { versions, url } = playlist
    const loadUrl = versions ? versions[0] : url
    return this.getUrl(
      loadUrl,
      {
        public: 1,
      },
      'getPublicPlaylistByPlaylistId',
    )
  }

  getPlaylistParams(playlist: Playlist): Promise<any> {
    return this.getPlaylist(
      playlist,
      {
        pageSize: 1,
      },
      'api.getPlaylistParams',
    )
  }

  getPlaylistVersionsByPlaylistId(playlistId: PlaylistId): Promise<any> {
    return this.getUrl(
      `${this.host}/api/playlist/${playlistId}/`,
      null,
      'api.getPlaylistVersionsByPlaylistId',
    )
  }

  getLastPlaylistVersionByPlaylistId(
    playlistId: PlaylistId,
    query:
      | {
          share: 1 | 0
        }
      | null
      | undefined,
  ): Promise<Playlist> {
    return this.getPlaylistVersionsByPlaylistId(playlistId).then(result => {
      const { versions } = result

      if (versions && versions.length > 0) {
        return this.getUrl(versions[0].url, query)
      } else {
        const error = new HttpError('Playlist not found.', 404, {})
        throw error
      }
    })
  }

  updateTrackArtwork(
    trackId: TrackId,
    artwork: null | File,
    overrideAlbumArtwork: boolean,
  ): Promise<any> {
    const additionalProps: RequestOptions =
      artwork !== null
        ? {
            attachments: [
              {
                field: 'new_artwork',
                file: artwork,
                filename: artwork.name,
              },
            ],
            fields: {},
          }
        : {
            fields: {
              new_artwork: 'null',
            },
          }

    if (!overrideAlbumArtwork) {
      additionalProps.fields = {
        ...additionalProps.fields,
        override_album_artwork: false,
      }
    }

    return this.patchUrl(
      `${this.host}/api/track/${trackId}/`,
      null,
      'api.updateTrackArtwork',
      additionalProps,
    )
  }

  sortPlaylistTracks(
    playlist: Playlist,
    key: string,
    direction: string,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/playlistversion/${playlist.id}/sort/`,
      {
        key: key,
        direction: direction,
      },
      'api.sortPlaylistTracks',
    )
  }

  showcaseOverrideImage(
    showcaseId: number,
    attachments: AttachedFile[],
  ): Promise<any> {
    const fields = {}
    attachments.forEach(({ field, file }) => {
      if (!file) {
        fields[field] = 'null'
      }
    })
    return this.patchUrl(
      `${this.host}/api/playlistversion/${showcaseId}/showcase_override_image/`,
      null,
      'api.showcaseOverrideImage',
      {
        attachments,
        fields,
      },
    )
  }

  // DEPRECATED
  bulkGetTracksMetadata(tracks: Track[]): Promise<any> {
    return this.postUrl(`${this.host}/api/track/metadata/`, {
      track_ids: tracks.map(t => t.id),
    })
  }

  async bulkUpdateTrackMetadata(
    source_track_id: TrackId | null | undefined,
    track_ids: Array<TrackId>,
    update: BulkUpdateOptions,
  ): Promise<void> {
    // Process each batch in sequence
    for (let i = 0; i < track_ids.length; i += BULK_UPDATE_BATCH_SIZE) {
      const chunk = track_ids.slice(i, i + BULK_UPDATE_BATCH_SIZE)
      await this.bulkUpdateBatchTrackMetadata(source_track_id, chunk, update)
    }
  }

  bulkUpdateBatchTrackMetadata(
    source_track_id: TrackId | null | undefined,
    track_ids: Array<TrackId>,
    update: BulkUpdateOptions,
  ): Promise<string> {
    return this.postUrl(
      `${this.host}/api/track/batch_metadata_edit/`,
      { ...update, track_ids, source_track_id },
      'api.bulkUpdateTrackMetadata',
    )
  }

  tracksBatchUpdate(
    update: Record<TrackId, ItemWithUpdatedFields>,
  ): Promise<string> {
    return this.postUrl(
      `${this.host}/api/track/batch_update/`,
      update,
      'api.tracksBatchUpdate',
    )
  }

  batchUpdateTrackArtwork(
    trackIds: TrackId[],
    artwork: File | null | undefined,
  ): Promise<any> {
    const additionalProps: RequestOptions = {
      fields: {
        track_ids: `[${trackIds.join(',')}]`,
      },
    }

    if (artwork !== null) {
      additionalProps.attachments = [
        {
          field: 'new_artwork',
          file: artwork || null,
          filename: artwork?.name,
        },
      ]
    } else {
      additionalProps.fields = {
        ...additionalProps.fields,
        new_artwork: 'null',
      }
    }

    return this.postUrl(
      `${this.host}/api/track/batch_artwork/`,
      null,
      'api.updateTrackArtwork',
      additionalProps,
    )
  }

  appendTracksToPlaylist(playlist: Playlist, tracks: Track[]): Promise<any> {
    const { playlist_id } = playlist
    const trackIds = tracks.map(track => track.id)
    return this.postUrl(
      `${this.host}/api/playlist/${playlist_id}/append_tracks/`,
      {
        track_ids: trackIds,
      },
      'api.appendTracksToPlaylist',
    )
  }

  removeTracksFromPlaylist(
    { playlist_id }: { playlist_id: PlaylistId },
    tracks: Track[],
  ): Promise<void> {
    return this.postUrl(
      `${this.host}/api/playlist/${playlist_id}/remove_tracks/`,
      {
        track_ids: tracks.map(track => track.id),
      },
      'api.removeTracksFromPlaylist',
    )
  }

  // themes
  _getThemeRequestParams(theme: Record<string, any>): RequestOptions {
    const { name, logo, image, defaultTheme } = theme
    const result: RequestOptions = {
      fields: {
        name,
        default: defaultTheme || false,
      },
      attachments: [],
    }
    if (logo === null) result.fields = { ...result.fields, logo: 'null' }
    if (image === null) result.fields = { ...result.fields, image: 'null' }

    if (logo) {
      result.attachments?.push({
        field: 'logo',
        file: logo,
        filename: logo.name,
      })
    }

    if (image) {
      result.attachments?.push({
        field: 'image',
        file: image,
        filename: image.name,
      })
    }

    return result
  }

  createTheme(
    theme: PlaylistTheme,
    progressEvent: (arg0: number) => void,
  ): Promise<any> {
    const { host } = this
    return this.postUrl(`${host}/api/playlisttheme/`, null, 'api.createTheme', {
      progressEvent: e => {
        if (progressEvent) progressEvent(e.percent)
      },
      ...this._getThemeRequestParams(theme),
    })
  }

  updateTheme(
    savedTheme: PlaylistTheme,
    updatedTheme: PlaylistTheme,
    progressEvent: ((arg0: number) => void) | null | undefined,
  ): Promise<any> {
    return this.patchUrl(savedTheme.url, null, 'api.updateTheme', {
      progressEvent: e => {
        if (progressEvent) progressEvent(e.percent)
      },
      ...this._getThemeRequestParams(updatedTheme),
    })
  }

  deleteTheme: (theme: PlaylistTheme) => Promise<void> = theme =>
    this.delUrl(theme.url, 'api.deleteTheme')
  getThemes: () => Promise<{
    themes: any
  }> = async () => {
    let url = null
    const themes: any[] = []

    do {
      const { results, next } = await this.getUrl<any>(
        url || `${this.host}/api/playlisttheme/`,
        null,
        'api.getThemes',
      )
      url = next
      themes.push(...results)
    } while (url)

    return {
      themes,
    }
  }
  // email groups
  getEmailGroups: (count?: number, offset?: number) => Promise<any> = (
    count?: number,
    offset?: number,
  ) =>
    this.getUrl(
      `${this.host}/api/savedrecipients/`,
      {
        count,
        offset,
      },
      'api.getEmailGroups',
    )

  getMailingLists: () => Promise<any> = () =>
    this.getUrl(
      `${this.host}/api/savedrecipients/`,
      { include_recipients: false },
      'api.getMailingLists',
    )

  getContactEmailGroups: (contactId: number) => Promise<any> = (
    contactId: number,
  ) =>
    this.getUrl(
      `${this.host}/api/v2/contact/${contactId}/mailing_lists/`,
      null,
      'api.getContactEmailGroups',
    )

  getEmailGroup(id: number): Promise<unknown> {
    return this.getUrl<any>(
      `${this.host}/api/savedrecipients/${id}/`,
      null,
      'api.getEmailGroup',
    ).then(result => {
      // normalize response
      const { businesses, recipients } = result

      if (businesses && recipients && businesses.length > 0) {
        const normalizedRecipients = recipients.map(recipient => {
          if (recipient.business_ids && recipient.business_ids.length > 0) {
            const recipientBusinesses = businesses.filter(business => {
              return recipient.business_ids.indexOf(business.id) > -1
            })
            return { ...recipient, businesses: recipientBusinesses }
          }

          return recipient
        })
        return { ...result, recipients: normalizedRecipients }
      }

      return result
    })
  }

  deleteEmailGroup: (group: RecipientList) => Promise<void> = group =>
    this.delUrl(
      `${this.host}/api/savedrecipients/${group.id}/`,
      'api.deleteEmailGroup',
    )
  createEmailGroup: (group: Partial<RecipientList>) => Promise<any> = (
    group: Partial<RecipientList>,
  ) =>
    this.postUrl(
      `${this.host}/api/savedrecipients/`,
      group,
      'api.createEmailGroup',
    )
  updateEmailGroup: (group: Partial<RecipientList>) => Promise<any> = group =>
    this.patchUrl(
      `${this.host}/api/savedrecipients/${group.id}/`,
      group,
      'api.updateEmailGroup',
    )
  alterRecipients: (
    listId: number,
    toAdd: Array<number>,
    toRemove: Array<number>,
  ) => Promise<any> = (listId: number, toAdd: number[], toRemove: number[]) =>
    this.postUrl(
      `${this.host}/api/savedrecipients/alter_recipients/`,
      {
        savedrecipients_ids: [listId],
        add_contact_ids: toAdd,
        remove_contact_ids: toRemove,
      },
      'api.alterRecipients',
    )
  // groups
  getNextGroups: (arg0: { results: Group[]; next: string }) => Promise<any> =
    async ({ results, next }) => {
      const response = await this.getUrl<any>(next, null, 'api.getNextGroups')

      if (response.next) {
        return this.getNextGroups({
          results: [...results, ...response.results],
          next: response.next,
        })
      } else {
        return [...results, ...response.results]
      }
    }
  getGroups: (
    since?: string | null | undefined,
    includeSharedChannels?: boolean | null | undefined,
  ) => Promise<Group[]> = async (since, includeSharedChannels) => {
    const params: GetGroupsParams = {
      include_shared_channels: includeSharedChannels,
    }

    if (since) {
      params.since = since
    }

    const result = await this.getUrl<any>(
      `${this.host}/api/group/`,
      params,
      'api.getGroups',
    )
    let results = result.results

    if (result.next && !since) {
      const allGroups = await this.getNextGroups(result)
      results = allGroups
    }

    return results
  }
  getGroup: (id: GroupId) => Promise<Group> = id =>
    this.getUrl(`${this.host}/api/group/${id}/`, null, 'api.getGroup')
  deleteGroup: (group: Group) => Promise<void> = group =>
    this.delUrl(`${this.host}/api/group/${group.id}/`, 'api.deleteGroup')
  leaveGroup: (group: Group) => Promise<{
    groups_ids: string[]
  }> = group =>
    this.postUrl(
      `${this.host}/api/group/${group.id}/leave_channel/`,
      null,
      'api.leaveGroup',
    )
  createGroup: (
    group: Partial<
      Group & {
        bulk_upload?: boolean
        meta?: string | null | undefined
      }
    >,
  ) => Promise<Group> = group =>
    this.postUrl(`${this.host}/api/group/`, group, 'api.createGroup')
  updateGroup: (group: Partial<Group>) => Promise<any> = group =>
    this.patchUrl(
      `${this.host}/api/group/${group.id}/`,
      group,
      'api.updateGroup',
    )
  shareGroup: (id: GroupId, options: Record<string, any>) => Promise<any> = (
    id,
    options,
  ) =>
    this.postUrl(
      `${this.host}/api/group/${id}/share/`,
      options,
      'api.shareGroup',
    )

  getGroupInvites: (id: GroupId) => Promise<Array<BusinessShare>> = async (
    id: GroupId,
  ) => {
    const result: { results: Array<BusinessShare> } = await this.getUrl(
      `${this.host}/api/group/${id}/invite/`,
      null,
      'api.getGroupInvites',
    )
    return result.results
  }
  sendGroupInvites: (
    id: GroupId,
    options: { subdomain: string },
  ) => Promise<BusinessShare> = (id, options) =>
    this.postUrl(
      `${this.host}/api/group/${id}/invite/`,
      options,
      'api.sendGroupInvites',
    )

  removeGroupInvites: (
    id: GroupId,
    options: {
      invite_id: number
    },
  ) => Promise<void> = (id, options) =>
    this.delUrl(
      `${this.host}/api/group/${id}/invite/${options.invite_id}/`,
      'api.removeGroupInvites',
    )
  // current business
  getCurrentBusiness: (
    params?:
      | {
          realtime_track_count: number
        }
      | null
      | undefined,
  ) => Promise<BusinessData> = (
    params?:
      | {
          realtime_track_count: number
        }
      | null
      | undefined,
  ): Promise<BusinessData> =>
    this.getUrl(
      `${this.host}/api/business/current/`,
      params,
      'api.getCurrentBusiness',
    )
  // current business
  getCurrentBusinessPublic: () => Promise<any> = () =>
    this.getUrl(
      `${this.host}/api/business/current/public/`,
      null,
      'api.getCurrentBusinessPublic',
    )
  getCurrentBusinessSubscription: () => Promise<SubscriptionData> =
    (): Promise<SubscriptionData> =>
      this.getUrl(
        `${this.host}/api/business/current/subscription/`,
        null,
        'api.getCurrentBusinessSubscription',
      )

  cancelCurrentBusinessSubscription: (options: {
    reason_fixed_choices: string
    reason_free_text: string
  }) => Promise<void> = (options): Promise<void> =>
    this.postUrl(
      `${this.host}/api/business/current/cancel_subscription/`,
      options,
      'api.cancelCurrentBusinessSubscription',
    )

  cancelChargebeeSubscription: (options: {
    reason_fixed_choices: string
    reason_free_text: string
  }) => Promise<void> = (options): Promise<void> =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/cancel_subscription/`,
      options,
      'api.cancelChargebeeSubscription',
    )

  // current business users management
  loadBusinessUsers(): Promise<BusinessUser[]> {
    return this.getUrl(
      `${this.host}/api/business_user/`,
      null,
      'api.loadBusinessUsers',
    )
  }

  changeBusinessUser(id: number, changes: object): Promise<any> {
    return this.putUrl(
      `${this.host}/api/business_user/${id}/`,
      changes,
      'api.changeBusinessUser',
    )
  }

  removeBusinessUser(id: number): Promise<void> {
    return this.delUrl(
      `${this.host}/api/business_user/${id}/`,
      'api.removeBusinessUser',
    )
  }

  // invite new business users & invitations management
  loadBusinessInvites(): Promise<ServerBusinessInvite[]> {
    return this.getUrl(`${this.host}/api/invite/`, null, 'api.loadInvites')
  }

  sendBusinessInvite(members: ServerBusinessInvite[]): Promise<any> {
    return this.postUrl(
      `${this.host}/api/invite/bulk_create/`,
      members,
      'api.sendInvite',
    )
  }

  resendBusinessInvite(
    inviteId: number,
    invite: Record<string, any>,
  ): Promise<any> {
    // API accepts different message (if necessary)
    return this.putUrl(
      `${this.host}/api/invite/${inviteId}/`,
      { ...invite, followup: true },
      'api.resendInvite',
    )
  }

  cancelBusinessInvite(inviteId: number): Promise<void> {
    return this.delUrl(
      `${this.host}/api/invite/${inviteId}/`,
      'api.resendInvite',
    )
  }

  // panes
  // TODO: add types for this API call
  //
  // request: search
  //   - results: Object[],
  //   - next: ?string,
  // request: playlist pane
  //   - tracks?: Track[],
  // request: track pane
  //   - nesting_parent_id: number,
  //   - playlist_versions: PlaylistVersion[],
  getPane(
    groupHeader: string,
    url: string,
    options?: {
      abortSignal?: AbortSignal
    },
  ): Promise<any> {
    /*
    if (url.indexOf('/playlistversion/') > -1) {
      const error = new Error('nope')
      error.status = 403
      return errorHandler(error, 'api.getPane', Promise.reject)
    }
    */
    return this.getUrl(url, null, 'api.getPane', {
      ...options,
      headers: {
        Accept: 'application/json',
        ...(groupHeader && {
          'X-Disco-GroupIDs': groupHeader,
        }),
      },
    })
  }

  /**
   * Fetches one page from `/api/browse/` — the bare-`type`, no-search-term
   * browse behind the mobile home "See all" lists. The browse response shape is
   * known, so this returns a typed envelope.
   */
  getBrowse(
    url: string,
    groups?: number[] | null,
    abortSignal?: AbortSignal,
  ): Promise<BrowseResponse> {
    return this.getUrl<BrowseResponse>(url, null, 'api.getBrowse', {
      headers: {
        Accept: 'application/json',
        // Scope the browse to explicit groups. Mobile passes its library groups
        // so the backend doesn't fall back to the "default groups" union, which
        // leaks un-imported inbox content,
        ...(groups && groups.length
          ? { 'X-Disco-GroupIDs': JSON.stringify(groups) }
          : {}),
      },
      abortSignal,
    })
  }

  _addSearchTerm(url: string, searchTerm?: string): string {
    if (!searchTerm) return url
    return `${url}&q=${searchTerm}&search_folders=true&include_playlist_tracks=true`
  }

  _buildViewUrl(
    type:
      | 'album'
      | 'artist'
      | 'playlist'
      | 'track'
      | 'brief'
      | 'briefInbox'
      | null,
    preparedSortParam: string,
    isPersonalLibrary?: boolean,
    count: number = 50,
    searchTerm?: string,
  ): string {
    const { host } = this

    switch (type) {
      case 'album':
        return `${host}/api/browse/?type=playlist&playlist_type=${type}&sort=${preparedSortParam}&count=${count}`

      case 'artist':
        // TODO: /artist/browse/ is not implemented yet
        return `${host}/api/artist/search/?count=${count}&sort=${preparedSortParam}`

      case 'playlist':
        return `${host}/api/browse/?type=playlist&sort=${preparedSortParam}&count=${count}`

      case 'track':
        return `${host}/api/browse/?type=track&count=${count}&sort=${preparedSortParam}${
          !searchTerm
            ? `&include_playlist_tracks=${String(!isPersonalLibrary)}`
            : ''
        }`

      case 'brief':
        return `${host}/api/briefs/?count=${count}`

      case 'briefInbox':
        return `${host}/api/briefs/${searchTerm}/submissions/?count=${count}`

      default:
        return `${host}/api/browse/?sort=${preparedSortParam}&count=${count}`
    }
  }

  _excludePlaylistTypes(
    url: string,
    excludePlaylistTypes?: string[] | null | undefined,
  ): string {
    if (excludePlaylistTypes && excludePlaylistTypes.length > 0) {
      const exclusionList = excludePlaylistTypes.join(',')
      return url + `&exclude_playlist_types=[${exclusionList}]`
    }

    return url
  }

  _addTagsFilter(url: string, tagsFilter?: string[]): string {
    if (tagsFilter) {
      const tagString = tagsFilter.join(',')
      return url + `&tags=${tagString}`
    }

    return url
  }

  _filterBriefsByTab(
    baseUrl: string,
    view: ViewType,
    searchTerm?: string,
  ): string {
    const search = searchTerm ? `&q=${searchTerm}` : ''
    if (view === BRIEF_OPEN_VIEW) {
      return `${baseUrl}&submission_status=open${search}`
    } else if (view === BRIEF_CLOSED_VIEW) {
      return `${baseUrl}&submission_status=closed${search}`
    } else if (view === BRIEF_MINE_VIEW) {
      return `${baseUrl}&user=me${search}`
    }

    return `${baseUrl}${search}`
  }

  getPaneUrl({
    groupType,
    view,
    sortParam,
    sortDirection,
    excludePlaylistTypes,
    tagsFilter,
    isPersonalLibrary,
    count,
    searchTerm,
  }: {
    groupType?: GROUP_TYPE
    view?: ViewType
    sortParam?: SORT
    sortDirection?: SORT_DIRECTION
    excludePlaylistTypes?: string[] | null | undefined
    tagsFilter?: string[]
    isPersonalLibrary?: boolean
    count?: number
    searchTerm?: string
  }): string {
    const defaultSortParam =
      view === TRACK_VIEW ? TRACK_DATE_SORT : PLAYLIST_DATE_SORT
    const sortParamString = sortParam ?? defaultSortParam
    const sortDirectionString = sortDirection ?? SORT_DIRECTION_ASC
    const preparedSortParam = getSortParam(
      groupType,
      sortParamString,
      sortDirectionString,
    )
    switch (view) {
      case TRACK_VIEW: {
        const baseUrl = this._buildViewUrl(
          'track',
          preparedSortParam,
          isPersonalLibrary,
          count,
          searchTerm,
        )
        const exclusionUrl = this._excludePlaylistTypes(
          baseUrl,
          excludePlaylistTypes,
        )

        const tagsFilterUrl = this._addTagsFilter(exclusionUrl, tagsFilter)
        return this._addSearchTerm(tagsFilterUrl, searchTerm)
      }

      case PLAYLIST_VIEW: {
        const baseUrl = this._buildViewUrl(
          'playlist',
          preparedSortParam,
          isPersonalLibrary,
          count,
          searchTerm,
        )

        const exclusionUrl = this._excludePlaylistTypes(
          baseUrl,
          excludePlaylistTypes,
        )

        const tagsFilterUrl = this._addTagsFilter(exclusionUrl, tagsFilter)
        return this._addSearchTerm(tagsFilterUrl, searchTerm)
      }

      case ALBUM_VIEW: {
        const baseUrl = this._buildViewUrl(
          'album',
          preparedSortParam,
          isPersonalLibrary,
          count,
          searchTerm,
        )

        const exclusionUrl = this._excludePlaylistTypes(
          baseUrl,
          excludePlaylistTypes,
        )

        const tagsFilterUrl = this._addTagsFilter(exclusionUrl, tagsFilter)
        return this._addSearchTerm(tagsFilterUrl, searchTerm)
      }

      case ARTIST_VIEW: {
        const baseUrl = this._buildViewUrl(
          'artist',
          preparedSortParam,
          isPersonalLibrary,
          count,
        )
        return this._addSearchTerm(baseUrl, searchTerm)
      }

      case BRIEF_ALL_VIEW:
      case BRIEF_CLOSED_VIEW:
      case BRIEF_MINE_VIEW:
      case BRIEF_OPEN_VIEW: {
        const briefsUrl = this._buildViewUrl(
          'brief',
          preparedSortParam,
          isPersonalLibrary,
          count,
        )
        return this._filterBriefsByTab(briefsUrl, view, searchTerm)
      }

      case BRIEF_INBOX_VIEW: {
        const briefInboxUrl = this._buildViewUrl(
          'briefInbox',
          preparedSortParam,
          isPersonalLibrary,
          count,
          searchTerm,
        )
        return briefInboxUrl
      }

      case DEFAULT_VIEW:
      default: {
        const baseType = groupType === CUSTOM_CHANNEL ? null : 'playlist'

        const baseUrl = this._buildViewUrl(
          baseType,
          preparedSortParam,
          isPersonalLibrary,
          count,
          searchTerm,
        )

        const exclusionUrl = this._excludePlaylistTypes(
          baseUrl,
          excludePlaylistTypes,
        )

        const tagsFilterUrl = this._addTagsFilter(exclusionUrl, tagsFilter)
        return this._addSearchTerm(tagsFilterUrl, searchTerm)
      }
    }
  }

  getPaneView(
    params: {
      groupHeader?: string
      groupType?: GROUP_TYPE
      view?: ViewType
      sortParam?: SORT
      sortDirection?: string
      excludePlaylistTypes?: string[] | null | undefined
      tagsFilter?: string[]
      isPersonalLibrary?: boolean
      count?: number
      searchTerm?: string
    },
    options?: {
      abortSignal?: AbortSignal
    },
  ): Promise<any> {
    const { groupHeader } = params
    const url = this.getPaneUrl(params)

    return this.getUrl(url, null, 'api.getPaneView', {
      ...options,
      headers: groupHeader
        ? {
            Accept: 'application/json',
            'X-Disco-GroupIDs': groupHeader,
          }
        : undefined,
    })
  }

  getRecentItems: () => Promise<{
    tracks: Track[]
    playlists: Playlist[]
    albums: Playlist[]
    artists: Artist[]
    channels: Group[]
  }> = () => {
    return this.getUrl(
      `${this.host}/api/recent_activity/`,
      {
        count: 3,
      },
      'api.getRecentItems',
    )
  }
  // user events
  getEventsByType: (type: string) => Promise<unknown> = (type: string) =>
    this.getUrl<any>(
      `${this.host}/api/user_event/`,
      {
        type,
      },
      'api.getEventsByType',
    ).then(result => result.results)
  sendEvent: (event: { event: string; type: string }) => Promise<any> =
    (event: { event: string; type: string }) =>
      this.postUrl(`${this.host}/api/user_event/`, event, 'api.sendEvent')
  getPlaylistStats: (playlistVersionId: PlaylistVersionId) => Promise<any> = (
    playlistVersionId: PlaylistVersionId,
  ) =>
    this.getUrl(
      `${this.host}/api/customer_stats/playlists/`,
      {
        playlistversion_id: playlistVersionId,
      },
      'api.getPlaylistStats',
    )

  getSummaryStats(
    params: {
      from?: string
      to?: string
    } = {},
  ): Promise<any> {
    return this.getUrl(
      `${this.host}/api/customer_stats/summary/`,
      params,
      'api.getSummaryStats',
    )
  }

  getChartStats(
    key: string,
    params: {
      from?: string
      to?: string
      user_type?: 'client' | null | undefined
    } = {},
  ): Promise<any> {
    return this.getUrl(
      `${this.host}/api/customer_stats/${key}/by_date/`,
      params,
      'api.getChartStats',
    )
  }

  getCustomerStats(
    key: string,
    kind: string,
    params: {
      count: number
      filter_prop: string
      from: string
      to: string
      user_type: string | null | undefined
    },
  ): Promise<any> {
    const sortParam = statsEndpoints[key][kind]
    const sort = `["-${sortParam}"]`
    return this.getUrl(
      `${this.host}/api/v2/customer_stats/${key}/`,
      { ...params, sort },
      'api.getCustomerStats',
    )
  }

  getPlaylistReport(query: object): Promise<DeferredPlaylistReport> {
    return this.getUrl(
      `${this.host}/api/customer_reports/`,
      query,
      'api.getPlaylistReport',
    )
  }

  deleteTrackFormat(trackId: TrackId, format: string): Promise<any> {
    return this.postUrl(
      `${this.host}/api/track/${trackId}/formats/`,
      {
        delete: [format],
      },
      'api.deleteTrackFormat',
    )
  }

  // bulk actions
  bulkDeleteTracks(
    trackIds: Array<TrackId>,
    source: string,
    pvId: PlaylistVersionId | null | undefined,
    deleteNested: boolean | null | undefined,
  ): Promise<any> {
    const id = cuid()
    return this.postUrl(
      `${this.host}/api/track/batch_delete/`,
      {
        track_ids: trackIds,
        source,
        pv: pvId || undefined,
        cuid: id,
        delete_nested: deleteNested,
      },
      'api.bulkDeleteTracks',
    )
  }

  bulkDeletePlaylists(playlistIds: PlaylistId[]): Promise<any> {
    return this.postUrl(
      `${this.host}/api/playlist/batch_delete/`,
      playlistIds,
      'api.bulkDeletePlaylists',
    )
  }

  // tag actions
  getTags(): Promise<any> {
    return this.getUrl(`${this.host}/api/tag/`, null, 'api.getTags')
  }

  bulkUpdatePlaylistTags(
    playlistIds: PlaylistId[],
    tags: string[],
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/playlist/alter_tags/`,
      {
        playlist_ids: playlistIds,
        update_tags: tags,
      },
      'api.updatePlaylistsTags',
    )
  }

  getNativeTags(): Promise<any> {
    return this.getUrl(
      `${this.host}/api/native_tag/`,
      null,
      'api.getNativeTags',
    )
  }

  createCustomNativeTag(
    name: NativeTag['name'],
    category_id: NativeTag['category_id'],
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/native_tag/`,
      {
        name,
        category_id,
      },
      'api.createCustomNativeTag',
    )
  }

  editCustomNativeTag(
    tagId: NativeTag['id'],
    data: Partial<NativeTag>,
  ): Promise<any> {
    return this.patchUrl(
      `${this.host}/api/native_tag/${tagId}/`,
      data,
      'api.editCustomNativeTag',
    )
  }

  deleteCustomNativeTag(tagId: NativeTag['id']): Promise<void> {
    return this.delUrl(
      `${this.host}/api/native_tag/${tagId}/`,
      'api.deleteCustomNativeTag',
    )
  }

  applyNativeTagsToTracks(
    track_ids: number[],
    tag_ids_to_add: number[],
    tag_ids_to_remove: number[],
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/native_tag/apply_to_tracks/`,
      {
        track_ids,
        tag_ids_to_add,
        tag_ids_to_remove,
      },
      'api.applyNativeTagsToTracks',
    )
  }

  // Artists
  getArtists(
    query: string,
    filter?: string,
    abortSignal?: AbortSignal,
  ): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: Artist[]
  }> {
    let params = {
      count: 50,
      q: query,
      exclude_related: 1,
    }

    if (filter) {
      params = { ...params, [filter]: 1 }
    }

    return this.getUrl(
      `${this.host}/api/artist/search/`,
      params,
      'api.getArtists',
      {
        abortSignal,
      },
    )
  }

  getRelatedArtists(artistId: ArtistId): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: Partial<Artist>[]
  }> {
    return this.getUrl(
      `${this.host}/api/artist/search/`,
      {
        count: 1000,
        related_to: artistId,
      },
      'api.getRelatedArtists',
    )
  }

  getArtist(artistId: ArtistId): Promise<Artist> {
    return this.getUrl(
      `${this.host}/api/artist/${artistId}/`,
      null,
      'api.getArtist',
    )
  }

  getArtistShowcases(artistId: ArtistId): Promise<{
    next: string | null | undefined
    results: Partial<Playlist>[]
  }> {
    return this.getUrl(
      `${this.host}/api/browse/`,
      {
        artist_id: artistId,
        is_showcase: true,
        type: 'playlist',
      },
      'api.getArtistShowcases',
    )
  }

  getArtistTracks(artistId: ArtistId): Promise<{
    next: string | null | undefined
    results: Partial<Track>[]
  }> {
    return this.getUrl(
      `${this.host}/api/v2/artist/${artistId}/tracks/`,
      {
        count: 50,
      },
      'api.getArtistTracks',
    )
  }

  updateArtist(
    artistId: ArtistId,
    data: Partial<Artist>,
  ): Promise<ClientLibrary> {
    return this.patchUrl(
      `${this.host}/api/artist/${artistId}/`,
      data,
      'api.updateArtist',
    )
  }

  updateArtistImage(
    artistId: ArtistId,
    image: File | null | undefined,
    progressEvent?: (percent: number) => unknown,
  ): Promise<string> {
    const additionalProps: RequestOptions = {
      fields: {},
      progressEvent: e => {
        if (progressEvent) progressEvent(e.percent)
      },
    }

    if (image === null) {
      additionalProps.fields = { ...additionalProps.fields, image: 'None' }
    } else if (image) {
      additionalProps.attachments = [
        {
          field: 'image',
          file: image,
          filename: image.name,
        },
      ]
    }

    return this.patchUrl(
      `${this.host}/api/artist/${artistId}/image/`,
      null,
      'api.updateArtistImage',
      additionalProps,
    )
  }

  artistSignup(data: Record<string, any>): Promise<any> {
    return this.postUrl(
      `${this.host}/api/artist_signup/`,
      data,
      'api.artistSignup',
    )
  }

  // Client Libraries
  createClientLibrary(data: { name: string }): Promise<ClientLibrary> {
    return this.postUrl(
      `${this.host}/api/client_library/`,
      data,
      'api.createClientLibrary',
    )
  }

  deleteClientLibrary(libraryId: ClientLibraryId): Promise<void> {
    return this.delUrl(
      `${this.host}/api/client_library/${libraryId}/`,
      'api.deleteClientLibrary',
    )
  }

  getClientLibraries(count?: number): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: ClientLibrary[]
  }> {
    let data: { count: number } | null = null

    if (count) {
      data = {
        count,
      }
    }

    return this.getUrl(
      `${this.host}/api/client_library/`,
      data,
      'api.getClientLibraries',
    )
  }

  getClientLibrary(libraryId: ClientLibraryId): Promise<ClientLibrary> {
    return this.getUrl(
      `${this.host}/api/client_library/${libraryId}/`,
      null,
      'api.getClientLibrary',
    )
  }

  updateClientLibrary(
    libraryId: ClientLibraryId,
    data: Partial<ClientLibrary>,
  ): Promise<ClientLibrary> {
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/`,
      data,
      'api.updateClientLibrary',
    )
  }

  updateClientLibraryImages(
    libraryId: ClientLibraryId,
    data: Partial<ClientLibrary>,
    progressEvent?: (percent: number) => unknown,
  ): Promise<string> {
    const additionalProps: RequestOptions = {
      fields: {},
      progressEvent: e => {
        if (progressEvent) progressEvent(e.percent)
      },
    }
    const images = {
      image_banner: data.image_banner_url,
      image_logo: data.image_logo_url,
    }
    Object.keys(images).forEach((key: string) => {
      const image = images[key]

      if (image === null) {
        additionalProps.fields = { ...additionalProps.fields, [key]: 'None' }
      } else if (image && image.name) {
        additionalProps.attachments = [
          ...(additionalProps.attachments || []),
          {
            field: key,
            file: image,
            filename: image.name,
          },
        ]
      }
    })
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/image/`,
      null,
      'api.updateClientLibraryImages',
      additionalProps,
    )
  }

  getClientLibraryContent(libraryId: ClientLibraryId): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: ClientLibraryContentItem[]
  }> {
    return this.getUrl(
      `${this.host}/api/client_library/${libraryId}/content/`,
      null,
      'api.getClientLibraryContent',
    )
  }

  alterClientLibraryGroups(
    libraryId: ClientLibraryId,
    to_add: ClientLibraryContentGroupInput[],
    to_remove: number[],
  ): Promise<string> {
    return this.postUrl(
      `${this.host}/api/client_library/${libraryId}/content/alter_discogroups/`,
      {
        to_add,
        to_remove,
      },
      'api.alterClientLibraryGroups',
    )
  }

  getClientLibraryArtists(
    libraryId: ClientLibraryId,
    query: string,
    isFeatured?: boolean,
  ): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: ClientLibraryArtist[]
  }> {
    return this.getUrl(
      `${this.host}/api/artist/search/`,
      {
        count: 50,
        client_library_id: libraryId,
        is_featured: isFeatured,
        q: query,
      },
      'api.getClientLibraryArtists',
    )
  }

  getClientLibraryArtist(
    libraryId: ClientLibraryId,
    artistId: ArtistId,
  ): Promise<ClientLibraryArtist> {
    return this.getUrl(
      `${this.host}/api/client_library/${libraryId}/artist/${artistId}/`,
      null,
      'api.getClientLibraryArtist',
    )
  }

  updateClientLibraryArtist(
    libraryId: ClientLibraryId,
    artistId: ArtistId,
    data: Partial<ClientLibraryArtist>,
  ): Promise<ClientLibraryArtist> {
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/artist/${artistId}/`,
      data,
      'api.updateClientLibraryArtist',
    )
  }

  getClientLibraryArtistTracks(
    libraryId: ClientLibraryId,
    artistId: ArtistId,
  ): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: Track[]
  }> {
    return this.getUrl(
      `${this.host}/api/client_library/${libraryId}/artist/${artistId}/track/`,
      {
        count: 50,
      },
      'api.getClientLibraryArtistTracks',
    )
  }

  updateClientLibraryArtistTracks(
    libraryId: ClientLibraryId,
    artistId: ArtistId,
    trackIds: TrackId[],
  ): Promise<string> {
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/set_artist_tracks/`,
      {
        artist_id: artistId,
        track_ids: trackIds,
      },
      'api.updateClientLibraryArtistTracks',
    )
  }

  getClientLibraryPlaylists(
    libraryId: ClientLibraryId,
    type: Playlist['playlist_type'] | null | undefined,
    query: string,
    isFeatured?: boolean,
  ): Promise<{
    facet: any[]
    next: string | null | undefined
    previous: string | null | undefined
    results: Playlist[]
  }> {
    return this.getUrl(
      `${this.host}/api/search/`,
      {
        client_library_id: libraryId,
        is_featured: isFeatured,
        playlist_type: type,
        q: query,
        return_tracks_artworks: 1,
        type: 'playlist',
      },
      'api.getClientLibraryPlaylists',
    )
  }

  alterClientLibraryPlaylists(
    libraryId: ClientLibraryId,
    to_add: number[],
    to_remove: number[],
  ): Promise<string> {
    return this.postUrl(
      `${this.host}/api/client_library/${libraryId}/playlist/alter_playlists/`,
      {
        to_add,
        to_remove,
      },
      'api.alterClientLibraryPlaylists',
    )
  }

  alterClientLibraryFeaturedPlaylists(
    libraryId: ClientLibraryId,
    to_add: number[],
    to_remove: number[],
  ): Promise<string> {
    return this.postUrl(
      `${this.host}/api/client_library/${libraryId}/alter_featured_playlists/`,
      {
        to_add,
        to_remove,
      },
      'api.alterClientLibraryFeaturedPlaylists',
    )
  }

  setClientLibraryFeaturedPlaylists(
    libraryId: ClientLibraryId,
    data: {
      album_ids?: number[]
      playlist_ids?: number[]
    },
  ): Promise<string> {
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/set_featured_playlists/`,
      data,
      'api.setClientLibraryFeaturedPlaylists',
    )
  }

  setClientLibraryFeaturedArtists(
    libraryId: ClientLibraryId,
    artistIds: ArtistId[],
  ): Promise<string> {
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/set_featured_artists/`,
      {
        artist_ids: artistIds,
      },
      'api.setClientLibraryFeaturedArtists',
    )
  }

  getClientLibraryTracks(
    libraryId: ClientLibraryId,
    query: string,
    isFeatured?: boolean,
    abortSignal?: AbortSignal,
  ): Promise<{
    facet: any[]
    next: string | null | undefined
    previous: string | null | undefined
    results: Track[]
  }> {
    return this.getUrl(
      `${this.host}/api/search/`,
      {
        client_library_id: libraryId,
        count: isFeatured ? 1000 : undefined,
        is_featured: isFeatured,
        type: 'track',
        q: query,
      },
      'api.getClientLibraryTracks',
      abortSignal ? { abortSignal } : undefined,
    )
  }

  async getClientLibraryFeaturedTracks(
    publicUrl: string,
    catalogId: ClientLibraryId,
    count: number,
  ): Promise<{
    next: string | undefined
    previous: string | undefined
    results: { tracks: Track[] }
  }> {
    try {
      const url = new URL(publicUrl)
      const apiHost = `${url.protocol}//${url.host}`

      const data = await this.getUrl<{
        next?: string
        previous?: string
        results?: { tracks: Track[] }
      }>(
        `${apiHost}/api/client_library/${catalogId}/search/`,
        {
          type: 'all',
          is_featured: '1',
          return_tracks_artworks: '1',
          count,
        },
        'api.getClientLibraryFeaturedTracks',
      )

      // Transform the response
      const tracks = data?.results?.tracks || []
      const tracksWithUrl = tracks.map((track: Track) => ({
        ...track,
        client_library_url: publicUrl,
      }))

      return {
        next: data?.next,
        previous: data?.previous,
        results: {
          tracks: tracksWithUrl,
        },
      }
    } catch (error) {
      throw new Error('Error: ' + (error?.message || error?.toString()))
    }
  }

  alterClientLibraryFeaturedTracks(
    libraryId: ClientLibraryId,
    to_add: number[],
    to_remove: number[],
  ): Promise<string> {
    return this.postUrl(
      `${this.host}/api/client_library/${libraryId}/alter_featured_tracks/`,
      {
        to_add,
        to_remove,
      },
      'api.alterClientLibraryFeaturedTracks',
    )
  }

  setClientLibraryFeaturedTracks(
    libraryId: ClientLibraryId,
    trackIds: number[],
  ): Promise<string> {
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/set_featured_tracks/`,
      {
        track_ids: trackIds,
      },
      'api.setClientLibraryFeaturedTracks',
    )
  }

  getLicenseRequestSubmissions({
    libraryId,
    ordering,
    offset,
    pageSize,
    fromDate,
    toDate,
  }: {
    libraryId: ClientLibraryId
    ordering: string
    offset: number
    pageSize: number
    fromDate?: string
    toDate?: string
  }): Promise<LicenseRequestSubmissionsPage> {
    let url = `${this.host}/api/client_library/${libraryId}/license_request/submissions/?count=${pageSize}&ordering=${ordering}&offset=${offset}`
    if (fromDate) url += `&from_date=${fromDate}`
    if (toDate) url += `&to_date=${toDate}`

    return this.getUrl(url, null, 'api.getLicenseRequestSubmissions')
  }

  getLicenseRequestSubmissionsExportUrl({
    libraryId,
    ordering,
    fromDate,
    toDate,
    selectedFields,
    selectedWriterFields,
  }: {
    libraryId: ClientLibraryId
    ordering: string
    selectedFields: string[]
    selectedWriterFields: string[]
    fromDate?: string
    toDate?: string
  }): string {
    let url = `${this.host}/api/client_library/${libraryId}/license_request/submissions_export/?ordering=${ordering}`

    const fieldsSearchParams = new URLSearchParams()

    selectedFields.forEach(mf => fieldsSearchParams.append('fields', mf))
    selectedWriterFields.forEach(wf =>
      fieldsSearchParams.append('writer_fields', wf),
    )

    if (fromDate) {
      fieldsSearchParams.append('from_date', fromDate.toString())
    }

    if (toDate) {
      fieldsSearchParams.append('to_date', toDate.toString())
    }

    if (fieldsSearchParams.size === 0) {
      return url
    }

    return url + '&' + fieldsSearchParams.toString()
  }

  getRequestFormVersion(formVersionId: number): Promise<RequestFormVersion> {
    return this.getUrl(
      `${this.host}/api/request_form_version/${formVersionId}/`,
      null,
      'api.getRequestFormVersion',
    )
  }

  // TODO: no 404
  getClientLibraryClients(libraryId: ClientLibraryId): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: ClientLibraryClient[]
  }> {
    return this.getUrl(
      `${this.host}/api/client_library/${libraryId}/client/`,
      null,
      'api.getClientLibraryClients',
    )
  }

  inviteClientLibraryClients(
    libraryId: ClientLibraryId,
    data: ClientLibraryInvite,
  ): Promise<ClientLibraryClient[]> {
    return this.postUrl(
      `${this.host}/api/client_library/${libraryId}/client/`,
      data,
      'api.inviteClientLibraryClients',
    )
  }

  resendClientLibraryInvite(
    libraryId: ClientLibraryId,
    clientId: number,
  ): Promise<ClientLibraryClient> {
    return this.postUrl(
      `${this.host}/api/client_library/${libraryId}/client/${clientId}/resend/`,
      null,
      'api.resendClientLibraryInvite',
    )
  }

  // TODO: Returns 204 No Content
  deleteClientLibraryClient(
    libraryId: ClientLibraryId,
    clientId: number,
  ): Promise<void> {
    return this.delUrl(
      `${this.host}/api/client_library/${libraryId}/client/${clientId}/`,
      'api.deleteClientLibraryClient',
    )
  }

  updateClientLibraryClient(
    libraryId: ClientLibraryId,
    clientId: number,
    data: Partial<ClientLibraryClient>,
  ): Promise<ClientLibraryClient> {
    return this.patchUrl(
      `${this.host}/api/client_library/${libraryId}/client/${clientId}/`,
      data,
      'api.updateClientLibraryClient',
    )
  }

  getClientLibraryStats(
    libraryId: ClientLibraryId,
    params: Record<string, any>,
  ): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: ClientLibraryClient[]
  }> {
    return this.getUrl(
      `${this.host}/api/client_library/${libraryId}/stats/`,
      params,
      'api.getClientLibraryStats',
    )
  }

  getClientLibraryDiscoveryPanel(
    query: string | null | undefined,
    filter: string | null | undefined,
    count?: number,
    sort?: string | null | undefined,
  ): Promise<{
    count: number
    next: string | null | undefined
    previous: string | null | undefined
    results: ClientLibraryDiscoveryPanelItem[]
  }> {
    let params: object = {
      count: count || 18,
      q: query || '',
    }

    if (filter) {
      params = { ...params, filter }
    }

    if (sort) {
      params = { ...params, sort }
    }

    return this.getUrl(
      `${this.host}/api/client_library_discovery_panel/`,
      params,
      'api.getClientLibraryDiscoveryPanel',
    )
  }

  clientLibraryDiscoveryPanelFavorite(
    libraryId: ClientLibraryId,
    is_favorite: boolean,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/client_library_discovery_panel/${libraryId}/favorite/`,
      {
        is_favorite,
      },
      'api.clientLibraryDiscoveryPanelFavorite',
    )
  }

  clientLibraryDiscoveryPanelRequestAccess(
    libraryId: ClientLibraryId,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/client_library_discovery_panel/${libraryId}/request_access/`,
      null,
      'api.clientLibraryDiscoveryPanelRequestAccess',
    )
  }

  getClientLibraryDiscoveryPanelById(
    libraryId: ClientLibraryId,
    tracksCount?: number,
  ): Promise<{ featured_tracks?: Track[] }> {
    const params = tracksCount ? `?tracks_count=${tracksCount}` : ''
    return this.getUrl(
      `${this.host}/api/client_library_discovery_panel/${libraryId}/${params}`,
      null,
      'api.getClientLibraryDiscoveryPanelById',
    )
  }

  getClientLibraryDiscoveryPanelSet(): Promise<any> {
    return this.getUrl(
      `${this.host}/api/client_library_discovery_panel_set/`,
      null,
      'api.getClientLibraryDiscoveryPanelSet',
    )
  }

  clientLibraryCuratedPlaylists(): Promise<any> {
    return this.getUrl(
      `${this.host}/api/client_library_curated_playlist/`,
      null,
      'api.clientLibraryCuratedPlaylists',
    )
  }

  clientLibraryCuratedPlaylistTracks(
    playlistId: number,
  ): Promise<{ tracks: Track[] }> {
    return this.getUrl(
      `${this.host}/api/client_library_curated_playlist/${playlistId}/`,
      null,
      'api.clientLibraryCuratedPlaylistTracks',
    )
  }

  // product updates
  getProductUpdates: () => Promise<{
    items: any
    next: string
  }> = () =>
    this.getUrl<any>(
      `${this.host}/api/product_update/`,
      null,
      'api.getProductUpdates',
    ).then(result => ({
      items: result.results,
      next: result.next,
    }))

  importContacts(
    contacts: Record<string, any>[],
    isCsv: boolean = false,
    add_to_savedrecipients_id: number | null | undefined = undefined,
  ): Promise<any> {
    return this.postUrl(
      `${this.host}/api/contact/bulk_import/?accept_deferred=1`,
      {
        contacts,
        add_to_savedrecipients_id: add_to_savedrecipients_id || undefined,
        is_csv: isCsv,
      },
      'api.importContacts',
    )
  }

  updateContacts(contacts: Partial<Contact>[]): Promise<any> {
    const whitelistedContacts = contacts.map(c =>
      pick(c, contactUpdatePropsWhitelist),
    )
    return this.postUrl(
      `${this.host}/api/contact/bulk_update/`,
      {
        contacts: whitelistedContacts,
      },
      'api.updateContacts',
    )
  }

  updateAllContacts(
    changes: ContactBulkChanges,
    options: {
      savedRecipientsId?: ContactId | null
      excludeIds?: ContactId[]
    } = {},
  ): Promise<{ updated_count: number; skipped_count: number }> {
    return this.postUrl(
      `${this.host}/api/contact/bulk_update/`,
      {
        all_contacts: true,
        changes,
        ...(options.savedRecipientsId
          ? { saved_recipients_id: options.savedRecipientsId }
          : {}),
        ...(options.excludeIds?.length
          ? { exclude_ids: options.excludeIds }
          : {}),
      },
      'api.updateAllContacts',
    )
  }

  deleteContacts(contacts: ContactId[]): Promise<any> {
    return this.postUrl(
      `${this.host}/api/contact/bulk_delete/`,
      {
        contacts,
      },
      'api.deleteContacts',
    )
  }

  unsubscribeContacts(
    unsubscribes: {
      id: number
      saved_recipients_id: number | null | undefined
    }[],
  ): Promise<any> {
    // saved_recipients_id: null = a global unsubscribe
    return this.postUrl(
      `${this.host}/api/contact/bulk_unsubscribe/`,
      unsubscribes,
      'api.unsubscribeContacts',
    )
  }

  getContacts(
    query?: string,
    sort?: string,
    recipientId?: number,
    count: number = 100,
  ): Promise<any> {
    return this.getUrl(
      `${this.host}/api/v2/contact/`,
      {
        q: query,
        sort: sort,
        count: count,
        include_deleted: 0,
        saved_recipients_id: recipientId,
      },
      'api.getContacts',
    )
  }

  getContactsByIds(ids: number[]): Promise<any> {
    return this.getUrl(
      `${this.host}/api/v2/contact/?ids=${ids.join(',')}`,
      null,
      'api.getContactsByIds',
    )
  }

  getContactsIds(
    query?: string,
    sort: string = 'name',
    recipientId?: number,
  ): Promise<any> {
    return this.getUrl(
      `${this.host}/api/v2/contact/`,
      {
        id_only: 1,
        include_deleted: 0,
        q: query,
        sort: sort,
        saved_recipients_id: recipientId,
      },
      'api.getContactsIds',
    )
  }

  getSingleContact(contactId: ContactId): Promise<any> {
    return this.getUrl(
      `${this.host}/api/v2/contact/${contactId}/`,
      null,
      'api.getSingleContact',
    )
  }

  getContactDetails(contactId: ContactId): Promise<any> {
    return Promise.all([
      this.getUrl<any>(
        `${this.host}/api/v2/contact/${contactId}/`,
        null,
        'api.getContactDetails',
      ),
      this.getContactActivity(contactId),
      this.getContactNotes(contactId),
    ]).then(([contact, activity, notes]) =>
      Promise.resolve({ ...contact, activity: activity, notes: notes || [] }),
    )
  }

  getContactActivity(contactId: ContactId): Promise<unknown> {
    return this.getUrl(
      `${this.host}/api/customer_stats/contact/?id=${contactId}`,
      null,
      'api.getContactActivity',
    )
  }

  exportContacts(ids?: ContactId[]): Promise<any> {
    return this.postUrl(
      `${this.host}/api/v2/contact/export/?include_deleted=0`,
      {
        contact_ids: ids,
      },
      'api.exportContacts',
    )
  }

  getContactNotes(contactId: ContactId): Promise<unknown> {
    return this.getUrl(
      `${this.host}/api/contact_note/?contact=${contactId}`,
      null,
      'api.getContactNotes',
    )
  }

  addContactNote(contactId: ContactId, content: ContactNote): Promise<any> {
    return this.postUrl(
      `${this.host}/api/contact_note/`,
      {
        contact: contactId,
        content,
      },
      'api.addContactNote',
    )
  }

  deleteContactNote: (noteId: number) => Promise<void> = (noteId: number) =>
    this.delUrl(
      `${this.host}/api/contact_note/${noteId}/`,
      'api.deleteContactNote',
    )
  updateContactNote: (
    contactId: ContactId,
    noteId: number,
    content: ContactNote,
  ) => Promise<any> = (
    contactId: ContactId,
    noteId: number,
    content: ContactNote,
  ) =>
    this.putUrl(
      `${this.host}/api/contact_note/${noteId}/`,
      {
        contact: contactId,
        content,
      },
      'api.updateContactNote',
    )
  exportMailingList: (id: number) => Promise<any> = (id: number) => {
    return this.getUrl(
      `${this.host}/api/savedrecipients/${id}/export/`,
      null,
      'api.exportMailingList',
    )
  }
  getWriters: (
    query: string,
    count?: number,
    ids?: Array<number>,
  ) => Promise<any> = (query: string, count?: number, ids?: number[]) =>
    this.getUrl(
      `${this.host}/api/v2/contact/`,
      {
        tags: 'Writer,Artist',
        count: count,
        q: query,
        ids: ids,
        include_deleted: 0,
        exact_name_match: 1,
      },
      'api.getWriters',
    )
  getPRO: () => Promise<any> = () =>
    this.getUrl(`${this.host}/api/pro/`, null, 'api.getPRO')
  addPro: (
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) => Promise<any> = (
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) =>
    this.postUrl(
      `${this.host}/api/pro/`,
      {
        name,
        ...(externalIdentifiers !== undefined && {
          external_identifiers: externalIdentifiers,
        }),
      },
      'api.addPro',
    )
  updatePro: (
    id: number,
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) => Promise<any> = (
    id: number,
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) =>
    this.patchUrl(
      `${this.host}/api/pro/${id}/`,
      {
        name,
        ...(externalIdentifiers !== undefined && {
          external_identifiers: externalIdentifiers,
        }),
      },
      'api.updatePro',
    )
  getPublishers: () => Promise<any> = () =>
    this.getUrl(`${this.host}/api/publisher/`, null, 'api.getPublisher')
  addPublisher: (
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) => Promise<any> = (
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) =>
    this.postUrl(
      `${this.host}/api/publisher/`,
      {
        name,
        ...(externalIdentifiers !== undefined && {
          external_identifiers: externalIdentifiers,
        }),
      },
      'api.addPublisher',
    )
  updatePublisher: (
    id: number,
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) => Promise<any> = (
    id: number,
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) =>
    this.patchUrl(
      `${this.host}/api/publisher/${id}/`,
      {
        name,
        ...(externalIdentifiers !== undefined && {
          external_identifiers: externalIdentifiers,
        }),
      },
      'api.updatePublisher',
    )
  addTrackWriter: (writer: any) => Promise<any> = (
    writer: Record<string, any>,
  ) =>
    this.postUrl(
      `${this.host}/api/v2/track_writer/`,
      writer,
      'api.addTrackWriter',
    )
  deleteWriterSplit: (splitId: number) => Promise<void> = (splitId: number) =>
    this.delUrl(
      `${this.host}/api/v2/track_writer/${splitId}/`,
      'api.deleteTrackWriterSplit',
    )
  updateWriterSplit: (splitId: number, split: any) => Promise<any> = (
    splitId: number,
    split: Record<string, any>,
  ) =>
    this.putUrl(
      `${this.host}/api/v2/track_writer/${splitId}/`,
      split,
      'api.updateWriterSplit',
    )
  revokeChannelShare: (channelId: GroupId) => Promise<any> = (
    channelId: GroupId,
  ) =>
    this.postUrl(
      `${this.host}/api/group/${channelId}/share_revoke/`,
      null,
      'api.revokeChannelShare',
    )
  joinChannel: (channelId: GroupId) => Promise<{
    groups: Group[]
  }> = (channelId: GroupId) =>
    this.postUrl(
      `${this.host}/api/group/${channelId}/join_channel/`,
      null,
      'api.acceptChannelInvite',
    )

  chargebeeRestartSubscription: () => Promise<any> = () =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/restart_subscription/`,
      null,
      'api.chargebeeRestartSubscription',
    )
  syncShortlistsState: (state: Array<any>) => Promise<any> = (
    state: Record<string, any>[],
  ) => {
    return this.postUrl(
      `${this.host}/api/shortlist/`,
      state,
      'api.syncShortlistsState',
    )
  }
  getServerTimeUrl: () => string = () => `${this.host}/api/current_datetime/`

  /* method for convinient loading of data required for routes, eg:
    requireData({
      contacts: [1, 2, 3],
      tracks: [1, 2, 3],
      artists: [1, 2, 3],
    })
  */
  requireData: (data: any) => Promise<unknown> | Promise<object> = (
    data: Record<string, any>,
  ) => {
    const keys = Object.keys(data)
    const promises = keys
      .map(key => {
        if (key === 'contacts' && data[key].length > 0) {
          return Promise.all(
            data[key].map(contactId => this.getContactDetails(contactId)),
          )
        }

        if (key === 'tracks' && data[key].length > 0) {
          return Promise.all(data[key].map(trackId => this.getTrack(trackId)))
        }

        if (key === 'artists' && data[key].length > 0) {
          return Promise.all(
            data[key].map(artistId => this.getArtist(artistId)),
          )
        }

        return Promise.reject(new Error(`Unknown data object key: ${key}`))
      })
      .filter(Boolean)

    if (!promises.length) {
      const error = new Error(
        'Invalid param, please provide object with list of objects to load',
      )
      return Promise.reject(error)
    }

    return Promise.all(promises).then(results => {
      const resultData: Record<string, any> = {}
      results.forEach((result, index) => {
        const key = keys[index]
        if (key) {
          resultData[key] = result
        }
      })
      return resultData
    })
  }
  // mfa
  createMfaAuthMethod: (method: MFAAuthMethod) => Promise<any> = (
    method: MFAAuthMethod,
  ) =>
    this.postUrl(
      `${this.host}/api/user-authentication-method/`,
      {
        method,
      },
      'api.createMfaAuthMethod',
    )
  getMfaAuthMethods: () => Promise<any> = () =>
    this.getUrl<any>(
      `${this.host}/api/user-authentication-method/`,
      null,
      'api.getMfaAuthMethods',
    ).then(result => result.results)
  validateMfaCode: (id: number, code: string) => Promise<any> = (
    id: number,
    code: string,
  ) =>
    this.postUrl(
      `${this.host}/api/user-authentication-method/${id}/verify/`,
      {
        mfa_code: code,
      },
      'api.validateMfaCode',
    )
  // constants
  getConstants: () => Promise<any> = () =>
    this.getUrl(`${this.host}/api/constants/`, null, 'api.getConstants').then(
      result => result,
    )
  allocateAddons: (params: {
    addons: ChargebeeAddon[]
    coupon: string
  }) => Promise<string> = async (params: {
    addons: ChargebeeAddon[]
    coupon: string
  }) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/allocate_addons/`,
      {
        allocate_request: params.addons, // TODO: deprecate allocate_request
        addons: params.addons, // opt-in to the new univeral OrderForm API type
        coupon: params.coupon,
      },
      'api.billing.allocateAddons',
    )

  chargebeeEstimateAddons: (params: {
    addons: ChargebeeAddon[]
    coupon?: string
  }) => Promise<ChargebeeAddonEstimateResponse> = async (params: {
    addons: ChargebeeAddon[]
    coupon?: string
  }) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/estimate_addons/`,
      {
        estimate_request: params.addons, // TODO: deprecate estimate_request
        addons: params.addons, // opt-in to the new univeral OrderForm API type
        ...(params.coupon ? { coupon: params.coupon } : {}),
      },
      'api.billing.chargebeeEstimateAddons',
    )

  chargebeeEstimateSignup: (data: {
    plan_id: string
    addons: object[]
    coupon: string | null | undefined
  }) => Promise<any> = (data: any) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/estimate_signup/`,
      data,
      'api.chargebeeEstimateSignup',
    )

  chargebeePerformSignup: (data: {
    plan_handle: string
    period_unit: string
    addons: object[]
    coupon: string
  }) => Promise<any> = (data: any) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/perform_signup/`,
      data,
      'api.chargebeePerformSignup',
    )

  // TODO: rename and extract from server.ts
  chargebeeListPlans: () => Promise<any> = () =>
    this.getUrl(
      `${this.host}/api/billing/chargebee/plans/`,
      null,
      'api.chargebeeListPlans',
    )

  chargebeeEstimatePlanChange: (data: {
    plan_id: string
    addons: object[]
  }) => Promise<any> = (data: any) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/estimate_plan_change/`,
      data,
      'api.chargebeeEstimatePlanChange',
    )

  chargebeePerformPlanChange: (data: {
    plan_id: string
    addons: object[]
    context: string
  }) => Promise<any> = (data: any) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/perform_plan_change/`,
      data,
      'api.chargebeePerformPlanChange',
    )

  getLeakTraces: (data: any) => Promise<any> = (data: any) =>
    this.getUrl(
      `${this.host}/api/watermarking_leak_trace/`,
      data,
      'api.getLeakTraces',
    )
  postLeakTraces: (data: any) => Promise<any> = (data: any) =>
    this.postUrl(
      `${this.host}/api/watermarking_leak_trace/`,
      data,
      'api.postLeakTraces',
    )
  createPaymentProfileChargebeeForm: (data: any) => Promise<any> = (
    data: any,
  ) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/update_billing_info/`,
      data,
      'api.createPaymentProfileChargebeeForm',
    )
  updateSubscriptionCustomer: (customer: any) => Promise<any> = (
    customer: Record<string, any>,
    // TODO: fix types
    // customer: {
    //   address: string,
    //   address_2: string,
    //   city: string,
    //   country: string,
    //   email: ?string,
    //   phone: ?number,
    //   state: ?string,
    //   vat_number: ?string,
    //   zip: string,
    // },
  ) =>
    this.postUrl(
      `${this.host}/api/billing/chargebee/update_customer_info/`,
      customer,
      'api.updateSubscriptionCustomer',
    )
  chargebeeListAddons: (
    plan_id?: string,
  ) => Promise<Record<string, ChargebeeAddOnDetailed[]>> = plan_id =>
    this.getUrl(
      `${this.host}/api/billing/chargebee/addons/` +
        (plan_id ? `?plan_id=${encodeURIComponent(plan_id)}` : ''),
      null,
      'api.chargebeeListAddons',
    )

  getGeoIP: () => Promise<GeoIpResponse | null | undefined> = (): Promise<
    GeoIpResponse | null | undefined
  > => this.getUrl(`${this.host}/api/geoip/`, null, 'api.getGeoIP')
  acceptCookies: (data: {
    necessary: boolean
    statistics: boolean
  }) => Promise<any> = (data: { necessary: boolean; statistics: boolean }) =>
    this.postUrl(`${this.host}/api/cookie_consent/`, data, 'api.acceptCookies')
  preloadHlsManifest: (
    url: string,
    headers: Record<string, any>,
  ) => Promise<any> = (url: string, headers: Record<string, any>) =>
    this.getUrl(url, null, 'api.prefetchHlsManifest', {
      headers: {
        Accept: '*/*',
        ...headers,
      },
    })
  sendSimilarityFeedback: (data: {
    feedback_type: string
    result_track_id: number
    query_track_id?: number
    query_youtube_url?: string
  }) => Promise<any> = (data: {
    result_track_id: number
    feedback_type: string
    query_track_id?: number
    query_youtube_url?: string
  }) =>
    this.postUrl(
      `${this.host}/api/similarity_search/feedback/`,
      data,
      'api.sendSimilarityFeedback',
    )
  contactUs: (arg0: {
    type: 'sales' | 'support'
    from_email: string
    subject: string
    body: string
    files?: File[]
  }) => Promise<void> = ({ files, ...fields }) =>
    this.postUrl(`${this.host}/api/contact_us/`, null, 'api.contactUs', {
      attachments: files
        ? files.map((file: File) => ({
            field: 'attachments',
            file,
          }))
        : undefined,
      fields,
    })
  exportTrackMetadata: (arg0: {
    start_date?: string
    end_date?: string
    playlist_version_id?: number
    group_id?: number
    export_nested_tracks: boolean
    export_native_tags?: boolean
    include_writers?: boolean
  }) => Promise<{
    deferred: boolean
    download_url: string
  }> = ({
    start_date = null,
    end_date = null,
    playlist_version_id = null,
    group_id = null,
    export_nested_tracks,
    export_native_tags = false,
    include_writers = false,
  }) =>
    this.postUrl(
      `${this.host}/api/track_metadata_export/`,
      {
        start_date,
        end_date,
        playlist_version_id,
        group_id,
        export_nested_tracks,
        export_native_tags,
        include_writers,
      },
      'api.exportTrackMetadata',
    )
  getCalendlyAvailableTimes = (): Promise<{
    nextSessionDate: Date
    nextSessionUrl: string
  }> =>
    this.getUrl(
      `${this.host}/api/calendly/available_times/`,
      null,
      'api.getCalendlyAvailableTimes',
    )
}

type GeoIpResponse = {
  is_eu: boolean
}
export const statsEndpoints = {
  playlists: {
    views: 'view_count',
    // most accessed playlists (by page views)
    downloads: 'download_count', // most downloaded playlists (by zip downloads)
  },
  tracks: {
    playbacks: 'play_count',
    // most streamed tracks, most played tracks (streams)
    pitches: 'pitch_count',
    // most pitched tracks (included in most saved playlists over the period)
    downloads: 'download_count', // most downloaded tracks
  },
  users: {
    playlists: 'playlist_versions_owned',
    // most playlists created by team member (over period)
    activity: 'all_activity', // most active end users (list of 5+)
  },
}
class ApiInstance {
  static #instance: Api

  private constructor() {}

  static getInstance() {
    if (!ApiInstance.#instance) {
      ApiInstance.#instance = new Api()
      ApiInstance.#instance.host =
        (typeof window !== 'undefined' && window.host) ||
        (typeof global !== 'undefined' && global.host) ||
        process.env.HOST ||
        ''
    }

    return ApiInstance.#instance
  }
}

const apiClient = ApiInstance.getInstance()

export default apiClient

export class RemoteApi extends Api {}

// export const RemoteApi = Api
